/**
 * i18n surface — every shipped string flows through `t(key, vars?)` from
 * day one (`00-vision.md` operating commitments + `30-design-system.md
 * §10.7.1`: "i18n architectural from day one … ICU MessageFormat as the
 * message format. Handles plural forms (Slavic, Arabic), gender variants,
 * parameterized strings, and the long tail of language-specific
 * complexity in a single contract").
 *
 * IV.C.1 / #82 — replaces the Walking-Skeleton variable-substitution
 * shim with `intl-messageformat`. The catalog file format is unchanged
 * (flat JSON of `{ key: ICU-string }`); existing call sites continue
 * working because plain `Hello {name}` is a strict subset of ICU.
 *
 * Compiled MessageFormat instances are cached per `(locale, key)` so a
 * busy fact-stream render only parses each ICU pattern once per session.
 *
 * The contract: NO hardcoded user-facing strings outside the catalog.
 * Reviewers should reject any UI literal that does not go through `t()`.
 */

import { IntlMessageFormat } from 'intl-messageformat';

// Options is the 4th constructor argument; `ignoreTag` lives in
// `ParserOptions` (re-exported as `Options` minus `locale`).
type MFOptions = NonNullable<ConstructorParameters<typeof IntlMessageFormat>[3]>;

const EN: Record<string, string> = {
  'app.title': 'dungeonlogs',
  // #013 — "Walking Skeleton" was internal milestone jargon; replace
  // with a user-facing tagline that says what the product is.
  'app.tagline': 'GM toolkit for living worlds',
  'app.brand_home_label': 'Go to entity list',
  // Boot gate — shown while the session probe is in flight, in place of the
  // app shell, so a signed-out visitor never sees the GM toolkit flash.
  'app.booting': 'Loading your campaign…',
  // Topbar search pill (opens the ⌘K command palette).
  'topbar.search_placeholder': 'Jump to entity, fact, session…',
  'topbar.search_label': 'Search and jump to anything (⌘K)',
  'topbar.actions': 'Actions',
  'topbar.actions_panel': 'Actions',
  // #007 (llui-hud) — accessible group label for the shared campaign-date +
  // as-of-scrubber container in the topbar.
  'topbar.dates': 'Campaign date and view-as-of controls',
  // Left navigation rail (per mocks/entity-page.html). Section labels +
  // the not-yet-built Work items shown disabled with a "coming soon" cue.
  'rail.nav_label': 'Primary navigation',
  'rail.recent': 'Recent',
  'rail.browse': 'Browse',
  'rail.work': 'Work',
  'rail.sessions': 'Sessions',
  'rail.maps': 'Maps',
  'rail.adventurers_book': 'Adventurers Book',
  'rail.coming_soon': 'Coming soon',
  'rail.browse_root_label': '{root} ({n})',
  // Trash (soft-deleted entities) — reachable from the rail Work section.
  'trash.open': 'Trash',
  'trash.heading': 'Trash',
  'trash.subtitle': 'Deleted entities are hidden from every view but kept in the op log — restore any of them here.',
  'trash.empty': 'Nothing in the Trash.',
  'trash.restore': 'Restore',
  'entities.list.create_label': 'Create a new {root}',
  'locations.title': 'Locations',
  'locations.empty': 'No locations yet. Create your first to start mapping the world.',
  'locations.new': 'New Location',
  // #141 — generic list / entity back-link strings. Used by
  // `viewLocationsList` (the multi-root list page heading after #132)
  // and `viewEntityPage` (the back-link copy must be root-agnostic so
  // a Character page doesn't read "← Locations").
  'entity.list.title': 'Entities',
  'entity.back': 'Back to entities',
  'entity.delete.action': 'Delete',
  'entity.delete.heading': 'Move to Trash?',
  'entity.delete.body':
    'Hide "{name}" and all its facts from every view. This is reversible — the entity and its history stay in the op log.',
  'entity.delete.cancel': 'Cancel',
  'entity.delete.confirm': 'Move to Trash',
  // #146 — entity-page header meta row (mock: "Location · 3 known names").
  'entity.meta.fact_count': '{n, plural, one {# fact} other {# facts}}',
  // Entity-page tabs (per 30 §7.2 "Overview is story-first; Facts is
  // the management view"). Reveals / History / Connections are
  // disabled placeholders until their features land.
  'entity.tab.overview': 'Overview',
  'entity.tab.facts': 'Facts',
  'entity.tab.reveals': 'Reveals',
  'entity.tab.issues': 'Issues',
  'entity.tab.history': 'History',
  'entity.tab.connections': 'Connections',
  // Per-entity auditor surface (Phase 2 / 04-llm-role.md §5)
  'entity.issues.badge': '{n, plural, one {# issue} other {# issues}}',
  'entity.issues.badge_label': 'View this entity’s auditor issues',
  'entity.issues.empty': 'No issues found for this entity.',
  'connections.empty': 'Nothing links here yet. When other entities reference this one, those incoming relationships appear here.',
  'connections.referenced_as': 'Referenced as “{label}” by',
  'entity.overview.connections_heading': 'Connections',
  // Causal chain (#222 / 06-timeline.md §8)
  'entity.overview.causal_chain_heading': 'Causal chain',
  'entity.overview.causal_chain_caption':
    'These events are linked by cause and precedence, ordered earliest first. This event is highlighted.',
  'entity.overview.causal_chain_cycle_note':
    'This chain contains a causal loop, so the order is only partial. Open the Auditor to resolve it.',
  // Recurring events — virtual instances (#236 / 06-timeline.md §10.3)
  'entity.overview.recurrences_heading': 'Recurring instances',
  'entity.overview.recurrences_caption':
    'Upcoming occurrences computed from this entity’s recurrence rules. These are projections, not yet real events.',
  // B4 (#237) — materialize a virtual instance into a real Event entity
  // (06-timeline.md §10.3). Copies the rule's defaults as canon facts; the
  // parent rule is untouched.
  'recurrence.materialize': 'Materialize',
  'entity.tab.list_label': 'Entity sections',
  // Honest empty state — shown only when the entity genuinely has no
  // renderable facts. No promise of an unbuilt "curated infobox".
  'entity.tab.overview.empty': 'No facts yet. Add some in the Facts tab to see them here.',
  'entity.overview.manage_link': 'Manage all facts in the Facts tab →',
  'entity.tab.facts.manage_hint': 'Showing all facts for this entity. Edit values inline.',
  // Phase 2 / 30 §7.2/§7.7 — Reveals tab (per-Viewer disclosure rollup).
  'entity.tab.reveals.hint':
    'Who can see what on this entity. Reveal individual facts from the Facts tab.',
  'entity.tab.reveals.visible_to': 'Visible to',
  'entity.tab.reveals.hidden_heading': 'Hidden from all (GM only)',
  'entity.tab.reveals.empty': 'No facts yet. Add some in the Facts tab, then reveal them here.',
  // #247 / 30 §7.14 — entity-level History / chronology tab. The whole-entity
  // timeline of fact changes, read from the op log (distinct from the per-fact
  // rollback panel on the Facts tab).
  'entity.tab.history.hint':
    'How this entity changed over the campaign — every recorded change to its facts, newest first.',
  'entity.tab.history.summary':
    '{changes, plural, one {# change} other {# changes}} across {predicates, plural, one {# fact} other {# facts}}',
  'entity.tab.history.empty':
    'No history yet. As you add and edit this entity’s facts, every change is recorded here.',
  // One chronology row. `value` is the folded value; `predicate` the label.
  'entity.history.entry': '{predicate} → {value}',
  'entity.history.meta': '{author} · {when}',
  'entity.history.latest': 'Current',
  'entity.history.past': 'Superseded',
  'entity.history.deleted': 'Removed',
  'entity.history.belief_label': 'Belief',
  // #134 — palette spans every root and accepts operator prefixes
  // (`type:<root>`, `is:<predicate>`). The placeholder is intentionally
  // short — the operator-prefix surface lives in the help affordance.
  'palette.placeholder': 'Search entities — type:<root>, is:<predicate>…',
  'palette.no_matches': 'No matches',
  'palette.help': 'Press ⌘K (Ctrl-K) to open · Esc to close',
  // Keyboard-hint footer (command-palette.html mock). Action labels are
  // localised; the key glyphs (↑↓, ↵, esc) are universal symbols.
  'palette.lane.entities': 'Entities',
  // The name lane is the only lane that ships in Phase 1 (FTS / graph /
  // vector arrive with the search-router migration). The badge + caption
  // mirror command-palette.html's `.palette-lane-h`.
  'palette.lane.name': 'name',
  'palette.lane.name_caption': 'Exact and prefix matches on entity names',
  // Per-result trail (command-palette.html `.result-trail`). The lane
  // tag is the non-color cue that pairs with the badge color; the "open"
  // hint surfaces on the active row only.
  'palette.result.lane_tag': 'name',
  'palette.result.open_hint': 'open',
  'palette.footer.navigate': 'Navigate',
  'palette.footer.select': 'Open',
  'palette.footer.dismiss': 'Dismiss',
  'palette.footer.aria': 'Keyboard shortcuts: arrow keys to navigate, Enter to open, Escape to dismiss',
  // Right-aligned summary in the footer (command-palette.html shows
  // "8 results · perspective: GM · 4 lanes"). Phase 1 ships the GM
  // perspective + the single name lane, so the summary reflects that.
  'palette.footer.summary':
    '{n, plural, =0 {No results · perspective: GM} one {# result · perspective: GM} other {# results · perspective: GM}}',
  // Accessible name for the inline esc affordance in the input row.
  'palette.input.dismiss_key': 'Press Escape to close',
  // Generic fallback placeholder for predicates without a bespoke
  // `entity.set_<id>` entry — i.e. every ADR-0004 opaque-id pack/homebrew
  // predicate. Keeps empty fields from leaking the raw i18n key.
  'entity.facts.set_value': 'Set {label}…',
  'entity.set_name': 'Set name',
  'entity.set_population': 'Set population',
  'entity.set_government': 'Set government',
  'entity.name': 'Name',
  'entity.population': 'Population',
  'entity.government': 'Government',
  'entity.history': 'History',
  'entity.history_heading': 'Fact history',
  'entity.rollback': 'Roll back to this version',
  'entity.invalid_number': 'Must be a number',
  // #133 — trait-driven fact-row editor. Per the ticket, row labels
  // read from `predicate.<id>.label` and placeholders from
  // `entity.set_<id>`. We provide entries for every predicate the
  // core pack ships so the renderer never falls back to the raw key.
  // Generic-medieval-fantasy pack predicates land in their own
  // section below.
  'entity.invalid_datetime_range_partial': 'Both start and end are required',
  'entity.invalid_datetime_range_order': 'End must be on or after start',
  'entity.multi.add_item': 'Add',
  'entity.multi.remove_item': 'Remove',
  // #156 — fact-row save affordance copy. Surfaces a dirty cue while
  // the input value differs from the substrate, an inline first-focus
  // hint, and a transient "saved ✓" confirmation. The hint is
  // dismissable; the dismissal persists in localStorage.
  'entity.facts.dirty_cue': 'Unsaved',
  'entity.facts.editing_cue': 'Editing',
  'entity.facts.saving_cue': 'Saving…',
  'entity.facts.saved_cue': 'Saved',
  'entity.facts.save_hint': 'Press Enter or click the Save button to save.',
  'entity.facts.save_hint_dismiss': 'Got it — hide this hint',
  'entity.facts.save_button': 'Save',
  'entity.facts.ai_draft': '✨ Draft with AI',
  'entity.facts.ai_drafting': '✨ Drafting…',
  'entity.facts.ai_image': '✨ Generate image',
  'entity.facts.ai_image_pending': '✨ Generating…',
  'entity.facts.ai_image_prompt_label': 'Image direction (optional)',
  'entity.facts.ai_image_prompt_placeholder': 'Optional: art style, framing, mood…',
  'entity.page.ai_draft': '✨ Draft with AI',
  'entity.page.ai_draft_heading': 'Draft with AI',
  'entity.page.ai_draft_help': '{n} empty field(s) will be drafted as proposals in the Draft Inbox for you to ratify.',
  'entity.page.ai_draft_prompt_label': 'Prompt (optional) — guides every field',
  'entity.page.ai_draft_prompt_placeholder': 'e.g. weathered, taciturn, hides a debt',
  'entity.page.ai_draft_submit': 'Draft {n} field(s)',
  'predicate.name.label': 'Name',
  'predicate.population.label': 'Population',
  'predicate.government.label': 'Government',
  'predicate.canonical_calendar.label': 'Canonical calendar',
  'predicate.display_calendars.label': 'Display calendars',
  'predicate.accent_color.label': 'Accent color',
  'predicate.hero_image.label': 'Hero image',
  'predicate.prose_voice.label': 'Prose voice',
  'predicate.setting_summary.label': 'Setting summary',
  'predicate.campaign_kind.label': 'Campaign kind',
  'predicate.ruled_by.label': 'Ruled by',
  'predicate.capital.label': 'Capital',
  'predicate.laws.label': 'Laws',
  'predicate.species.label': 'Species',
  'predicate.gender.label': 'Gender',
  'predicate.appearance.label': 'Appearance',
  'predicate.portrait.label': 'Portrait',
  'predicate.born_in.label': 'Born',
  'predicate.died_in.label': 'Died',
  'predicate.cause_of_death.label': 'Cause of death',
  'predicate.claim_to_fame.label': 'Claim to fame',
  'predicate.reputation.label': 'Reputation',
  'predicate.known_for.label': 'Known for',
  'predicate.magical_properties.label': 'Magical properties',
  'predicate.school.label': 'School',
  'predicate.bound_to.label': 'Bound to',
  'predicate.occurred_at.label': 'Occurred at',
  'predicate.participants.label': 'Participants',
  'predicate.location.label': 'Location',
  'predicate.objective.label': 'Objective',
  'predicate.reward.label': 'Reward',
  'predicate.quest_status.label': 'Quest status',
  'predicate.giver.label': 'Quest giver',
  'predicate.real_world_span.label': 'Real-world span',
  'predicate.summary.label': 'Summary',
  'predicate.session_number.label': 'Session number',
  'predicate.pending_auto_ratify_policy.label': 'Pending auto-ratify policy',
  'predicate.controlled_by.label': 'Controlled by',
  'predicate.visited.label': 'Visited',
  'predicate.class.label': 'Class',
  'predicate.level.label': 'Level',
  'predicate.alignment.label': 'Alignment',
  'predicate.currency_value.label': 'Currency value',
  'predicate.patron_deity.label': 'Patron deity',
  'entity.set_canonical_calendar': 'Pick a canonical calendar',
  'entity.set_display_calendars': 'Pick display calendars',
  'entity.set_accent_color': 'Set accent color',
  'entity.set_hero_image': 'Set hero image asset id',
  'entity.set_prose_voice': 'Pick a prose voice',
  'entity.set_setting_summary': 'Write the setting summary',
  'entity.set_campaign_kind': 'Set the campaign kind',
  'entity.set_ruled_by': 'Set the ruler',
  'entity.set_capital': 'Set the capital',
  'entity.set_laws': 'Set the laws',
  'entity.set_species': 'Set species',
  'entity.set_gender': 'Set gender',
  'entity.set_appearance': 'Describe the appearance',
  'entity.set_portrait': 'Set portrait asset id',
  'entity.set_born_in': 'Set date of birth',
  'entity.set_died_in': 'Set date of death',
  'entity.set_cause_of_death': 'Set cause of death',
  'entity.set_claim_to_fame': 'Set claim to fame',
  'entity.set_reputation': 'Set reputation',
  'entity.set_known_for': 'Set what they are known for',
  'entity.set_magical_properties': 'Describe magical properties',
  'entity.set_school': 'Pick a school',
  'entity.set_bound_to': 'Set what it is bound to',
  'entity.set_occurred_at': 'Set when it occurred',
  'entity.set_participants': 'Set participants',
  'entity.set_location': 'Set the location',
  'entity.set_objective': 'Set the objective',
  'entity.set_reward': 'Set the reward',
  'entity.set_quest_status': 'Set quest status',
  'entity.set_giver': 'Set the quest giver',
  'entity.set_real_world_span': 'Set the real-world span',
  'entity.set_summary': 'Write a summary',
  'entity.set_session_number': 'Set session number',
  'entity.set_pending_auto_ratify_policy': 'Set pending auto-ratify policy',
  'entity.set_controlled_by': 'Set the controller',
  'controller.heading': 'Assign a controller',
  'controller.hint': 'Bind a campaign member to this character. They will see the world as this PC.',
  'controller.assigned': 'Controller: {name}',
  'controller.empty': 'No members yet — invite a player to the campaign first.',
  'controller.clear': 'Clear controller',
  'controller.cancel': 'Cancel',
  'entity.set_visited': 'Set what is visited',
  'entity.set_class': 'Set class',
  'entity.set_level': 'Set level',
  'entity.set_alignment': 'Set alignment',
  'entity.set_currency_value': 'Set currency value',
  'entity.set_patron_deity': 'Set patron deity',
  // Enum-value labels for the `prose_voice` predicate (the only
  // closed enum the core pack ships at Phase 1 scope).
  'enum.prose_voice.default': 'Default',
  'enum.prose_voice.gritty': 'Gritty',
  'enum.prose_voice.whimsical': 'Whimsical',
  'enum.prose_voice.pulp': 'Pulp',
  'enum.prose_voice.noir': 'Noir',
  'enum.prose_voice.high_fantasy': 'High fantasy',
  'connection.offline': 'Offline — edits are queued and will sync when the connection returns.',
  'connection.syncing': 'Syncing…',
  'entities.syncing': 'Syncing campaign from the server…',
  // Substrate-storage banner copy. Distinct from `connection.offline`
  // because the substrate state describes LOCAL persistence, not
  // network reachability. A Chrome 148 user whose OPFS failed has a
  // working network and a broken local replica — the offline pill
  // alone is too subtle for that catastrophic state.
  'substrate.banner.failed':
    'Storage unavailable — saves will not work. Check the browser console for details.',
  // The local OPFS cache fell back to in-memory, but the SyncClient
  // still streams every op to the server (DOSQL) within milliseconds
  // of typing. On a healthy connection, edits ARE preserved across
  // reload because the server replays them — what's actually at risk
  // is unsynced offline edits.
  'substrate.banner.memory':
    "Local cache unavailable — running on in-memory storage. Synced edits are safe on the server; offline edits made while disconnected may not survive a reload. (Your browser's OPFS storage didn't open.)",
  'substrate.banner.why': 'Why?',
  'substrate.banner.why_aria': 'Show storage requirement details',
  'substrate.dialog.heading': 'About local storage',
  'substrate.dialog.requirements':
    'dungeonlogs keeps a local SQLite replica in your browser\'s OPFS for offline edits and snappy reads. OPFS requires Chrome 102+, Safari 16.4+, or Firefox 111+; private-mode windows on some browsers disable it. When OPFS is unavailable, the app keeps working against the server — synced edits are safe — but offline edits made while disconnected may not survive a reload.',
  'substrate.dialog.error_label': 'Error',
  'substrate.dialog.opfs_cause_label': 'Underlying OPFS error',
  'substrate.dialog.close': 'Close',
  // Generic modal close affordance — the @dl/ui/modal atom needs an
  // accessible label for the backdrop scrim (a real <button>); each
  // call site passes this via labels.close. Specific dialogs pass
  // their own close label (e.g. substrate.dialog.close) where the
  // semantics differ.
  'modal.close': 'Close',
  'substrate.save_failed_no_substrate':
    'Could not save — local storage is unavailable. See the banner at the top of the page.',
  'substrate.save_failed_generic': 'Could not save: {error}',
  'substrate.save_failed_no_fact':
    'Could not set the date — this statement no longer exists. Reload the page and try again.',
  // ICU pluralisation — the WS shim only ran a regex; the upgrade keeps
  // the same call signature but renders correct singular/plural variants.
  'connection.queue_pending':
    '{n, plural, =0 {No edits waiting to sync} one {# edit waiting to sync} other {# edits waiting to sync}}',
  'connection.conflict_pending':
    '{n, plural, =0 {No edits need your attention} one {# edit needs your attention} other {# edits need your attention}}',
  'connection.use_mine': 'Keep mine',
  'connection.discard_mine': 'Discard mine',
  // Design-system theme toggle (per 30-design-system.md §3 — light /
  // dark / system tri-state cycle). The label is read out by SR and
  // surfaced as the button tooltip; the icon flips automatically.
  'theme.light': 'Theme: light. Click to switch to dark.',
  'theme.dark': 'Theme: dark. Click to follow system.',
  'theme.system': 'Theme: system. Click to switch to light.',
  'theme.label': 'Theme',
  'settings.title': 'Campaign settings',
  'settings.open': 'Settings',
  'settings.pending_policy.heading': 'Pending fact auto-ratify',
  'settings.pending_policy.help':
    'How player-authored "pending" facts behave when a session ends. The LLM never authors pending facts (07-session-experience.md §8.6) — this controls player-originated content only.',
  'settings.pending_policy.option.manual':
    'Manual — pending stays pending until you ratify or reject (default)',
  'settings.pending_policy.option.auto_ratify':
    'Auto-ratify — eligible pending facts promote to canon at session close',
  'settings.pending_policy.option.appear_as_pending':
    'Appear as pending — pending persists past session close with a cleanup chip',
  // Per-campaign AI prompt extensions (GM-managed).
  'settings.ai.heading': 'AI prompt extensions',
  'settings.ai.help':
    'Campaign-specific guidance added on top of the global prompt templates. The campaign-wide note applies to every AI task; per-task additions target one kind of generation.',
  'settings.ai.loading': 'Loading…',
  'settings.ai.save': 'Save',
  'settings.ai.dirty': 'Unsaved changes',
  'settings.ai.saved': 'Saved',
  'settings.ai.placeholder': 'e.g. Grimdark setting — avoid humor; names sound Slavic.',
  'settings.ai.scope.global': 'Campaign-wide note (all AI tasks)',
  'settings.ai.scope.prose_suggest': 'Prose drafting addition',
  'settings.ai.scope.draft_entity': 'Multi-field drafting addition',
  'settings.ai.scope.image_generate': 'Image generation addition',
  'settings.ai.scope_help.global':
    'Appended to every AI task — tone, setting, naming conventions, content to avoid.',
  'settings.ai.scope_help.prose_suggest': 'Only the ✨ Enrich / single-paragraph prose drafts.',
  'settings.ai.scope_help.draft_entity': 'Only the ✨ Draft-with-AI multi-field generation.',
  'settings.ai.scope_help.image_generate': 'Only image prompts — art style, medium, framing.',
  // #schema-settings — tabbed settings page.
  'settings.tab.list_label': 'Settings sections',
  'settings.tab.general': 'General',
  'settings.tab.campaign': 'Campaign',
  'settings.tab.traits': 'Custom traits & predicates',
  'settings.tab.types': 'Custom entity types',
  'settings.tab.calendars': 'Custom calendars',
  // #177 / ADR-0004 §1/§4 — custom-calendar authoring on the Calendars tab.
  'calendar_settings.empty': 'No calendars resolve for this campaign.',
  'calendar_settings.set_primary': 'Set as primary',
  'calendar_settings.retire': 'Retire',
  'calendar_settings.actions_for': 'Actions for {name}',
  // #232 / §4.1 — close an open cycle (ASOIAF seasons) at the in-world cursor.
  'calendar_settings.close_cycle_at': 'Close {cycle} at day {day}',
  'calendar_settings.close_cycle_no_cursor': 'Close {cycle} (set an in-world date first)',
  'calendar_settings.close_cycle_for': 'Close open cycle {cycle} for {name}',
  'calendar_settings.summary.months': '{count} months',
  'calendar_settings.summary.week': '{count}-day week',
  'calendar_settings.summary.leap': 'leap',
  'calendar_settings.new_month': 'New month',
  'calendar_settings.new_era': 'New era',
  'calendar_settings.new_weekday': 'Day',
  'calendar_settings.new_period': 'New period',
  'calendar_settings.sample.month_1': 'First Month',
  'calendar_settings.sample.month_2': 'Second Month',
  'calendar_settings.sample.month_3': 'Third Month',
  'calendar_settings.sample.era_name': 'Common Era',
  // #186 / ADR-0004 §6 — typed Campaign Settings view (config.set ops).
  'settings.campaign.unset': '— not set —',
  'settings.campaign.world_name.label': 'World name',
  'settings.campaign.world_name.help':
    'The name of the world this campaign is set in. Shown across the campaign and used to ground AI generation.',
  'settings.campaign.world_name.placeholder': 'e.g. Eberron, Mistral, the Shattered Reach',
  'settings.campaign.accent_color.label': 'Accent color',
  'settings.campaign.accent_color.help':
    'An sRGB hex color used for this campaign’s theming. The hex value is shown alongside the swatch so color is never the only cue.',
  'settings.campaign.campaign_kind.label': 'Campaign kind',
  'settings.campaign.campaign_kind.help':
    'How this campaign is shaped. A classification only — it never gates a feature.',
  'settings.campaign.campaign_kind.option.one_shot': 'One-shot',
  'settings.campaign.campaign_kind.option.mini_arc': 'Mini-arc',
  'settings.campaign.campaign_kind.option.long_running': 'Long-running',
  'settings.campaign.campaign_kind.option.sandbox': 'Sandbox',
  'settings.campaign.campaign_kind.option.episodic': 'Episodic',
  'settings.campaign.status.label': 'Campaign status',
  'settings.campaign.status.help':
    'Active campaigns are live; archived campaigns become read-mostly. You can change this at any time.',
  'settings.campaign.status.option.active': 'Active',
  'settings.campaign.status.option.archived': 'Archived',
  'settings.campaign.prose_voice.label': 'Prose voice',
  'settings.campaign.prose_voice.help':
    'The tone the AI uses when drafting prose for this campaign. AI is always optional — this only shapes generated text.',
  'settings.campaign.prose_voice.option.default': 'Default',
  'settings.campaign.prose_voice.option.gritty': 'Gritty',
  'settings.campaign.prose_voice.option.whimsical': 'Whimsical',
  'settings.campaign.prose_voice.option.pulp': 'Pulp',
  'settings.campaign.prose_voice.option.noir': 'Noir',
  'settings.campaign.prose_voice.option.high_fantasy': 'High fantasy',
  'settings.campaign.llm_kill_switch.label': 'AI generation',
  'settings.campaign.llm_kill_switch.help':
    'Instantly stop all AI generation for this campaign — prose, draft entities, images, embeddings. Every non-AI feature keeps working. Re-enable any time; takes effect immediately.',
  'settings.campaign.llm_kill_switch.toggle': 'Disable all AI generation for this campaign',
  'settings.campaign.llm_kill_switch.on': '● AI generation is ON',
  'settings.campaign.llm_kill_switch.off': '✕ AI generation is OFF for this campaign',
  'settings.campaign.llm_pending_lane_mode.label': 'LLM pending lane',
  'settings.campaign.llm_pending_lane_mode.help':
    'Auto-promote allowlisted LLM drafts to “pending” mid-session so the table can see them. The LLM never writes canon — promotion is a separate system action, and promoting reveals nothing to players (a pending fact stays GM-private until you reveal it).',
  'settings.campaign.llm_pending_lane_mode.option.off':
    'Off — eligible drafts stay in the inbox',
  'settings.campaign.llm_pending_lane_mode.option.surface':
    'Surface — promote and bump the pending count',
  'settings.campaign.llm_pending_lane_mode.option.silent':
    'Silent — promote without a notification',
  'settings.campaign.llm_pending_allowlist.label': 'Pending-lane allowlist',
  'settings.campaign.llm_pending_allowlist.help':
    'Predicate ids whose LLM drafts may auto-promote when the lane is active. Comma-separated. Built-in “name” is never eligible.',
  'settings.campaign.llm_pending_allowlist.placeholder': 'e.g. pred_population, pred_appearance',
  'settings.campaign.gm_only_drafts.label': 'GM-only entities',
  'settings.campaign.gm_only_drafts.help':
    'Entity ids whose LLM drafts stay GM-only — never auto-promoted, even when their predicate is allowlisted. Comma-separated. Use for sensitive NPCs, secret factions, plot-critical lore.',
  'settings.campaign.gm_only_drafts.placeholder': 'e.g. npc_villain, faction_cabal',
  'settings.campaign.setting_summary.label': 'Setting summary',
  'settings.campaign.setting_summary.help':
    'A private blurb describing the setting. Used to ground AI generation; never shown to players as canon.',
  'settings.campaign.setting_summary.placeholder':
    'e.g. A drowned empire of canal-cities ruled by merchant houses; magic is rare and feared.',
  'settings.campaign.canonical_calendar.label': 'Canonical calendar',
  'settings.campaign.canonical_calendar.help':
    'The in-world calendar that drives the campaign date. Choose from the bundled calendars or any you have defined.',
  'settings.campaign.display_calendars.label': 'Additional display calendars',
  'settings.campaign.display_calendars.help':
    'Other calendars shown alongside the canonical one (e.g. a regional reckoning). Toggle each on or off.',
  'settings.campaign.hero_image.label': 'Hero image',
  'settings.campaign.hero_image.help':
    'The campaign’s banner image. Enter an asset id; full upload arrives with the asset pipeline.',
  'settings.campaign.hero_image.placeholder': 'asset id',
  'settings.campaign.locale.label': 'Locale',
  'settings.campaign.locale.help':
    'A BCP-47 locale tag used to format dates, numbers, and units for this campaign.',
  'settings.campaign.locale.placeholder': 'e.g. en-US, fr-FR, ja-JP',
  'settings.campaign.time_zone.label': 'Time zone',
  'settings.campaign.time_zone.help':
    'An IANA time-zone id used to display real-world session times.',
  'settings.campaign.time_zone.placeholder': 'e.g. America/New_York, Europe/Paris',
  // GM preference profile (#250) — the explicit, GM-authored style layer + the
  // opt-in gate for the inferred (signals-derived) layer.
  'settings.campaign.gm_pref.heading': 'GM preference profile',
  'settings.campaign.gm_pref.help':
    'Shape how the AI writes for you. These notes are added to every AI generation as style guidance — they never become canon and are never shown to players.',
  'settings.campaign.gm_pref.tone.label': 'Tone',
  'settings.campaign.gm_pref.tone.help': 'Formal, terse, evocative, clinical — the register you prefer.',
  'settings.campaign.gm_pref.tone.placeholder': 'e.g. terse and dry; understated menace',
  'settings.campaign.gm_pref.naming.label': 'Naming conventions',
  'settings.campaign.gm_pref.naming.help': 'Preferred patterns, cultural roots, forbidden patterns.',
  'settings.campaign.gm_pref.naming.placeholder': 'e.g. Norse roots; avoid apostrophes',
  'settings.campaign.gm_pref.vocabulary.label': 'Vocabulary',
  'settings.campaign.gm_pref.vocabulary.help': 'Words to favour, words to avoid.',
  'settings.campaign.gm_pref.vocabulary.placeholder': 'e.g. favour concrete nouns; avoid “epic”',
  'settings.campaign.gm_pref.pacing.label': 'Pacing',
  'settings.campaign.gm_pref.pacing.help': 'Description density, dialogue length, scene rhythm.',
  'settings.campaign.gm_pref.pacing.placeholder': 'e.g. tight scenes, short dialogue',
  'settings.campaign.gm_pref.style_references.label': 'Style references',
  'settings.campaign.gm_pref.style_references.help': 'Named authors, settings, or aesthetics you enjoy.',
  'settings.campaign.gm_pref.style_references.placeholder': 'e.g. Le Guin, Gene Wolfe',
  'settings.campaign.gm_pref.content_boundaries.label': 'Content boundaries',
  'settings.campaign.gm_pref.content_boundaries.help':
    'A hard line the AI must respect — never inferred, always honoured.',
  'settings.campaign.gm_pref.content_boundaries.placeholder': 'e.g. fade to black on violence',
  'settings.campaign.gm_pref.free_form.label': 'Free-form notes',
  'settings.campaign.gm_pref.free_form.help': 'Anything else about your style.',
  'settings.campaign.gm_pref.free_form.placeholder':
    'e.g. morally-gray villains; low-magic; avoid common D&D tropes',
  'settings.campaign.gm_pref.inference.label': 'Learn from my edits',
  'settings.campaign.gm_pref.inference.help':
    'Off by default. When on, the AI infers preferences from which drafts you accept, edit, or reject and adds them to your profile. You stay in control — every inferred entry is inspectable, and you can turn this off any time.',
  'settings.campaign.gm_pref.inference.toggle': 'Let the AI learn my style from my edits',
  'settings.campaign.gm_pref.inference.on': '● Inference is ON',
  'settings.campaign.gm_pref.inference.off': '✕ Inference is OFF (off by default)',
  // Traits/predicates CRUD tab.
  'settings.traits.heading': 'Custom traits',
  'settings.traits.help':
    'Homebrew traits defined for this campaign. A trait bundles predicates that attach together to an entity. Editing or retiring a trait is non-destructive — facts already written persist in the op log.',
  'settings.traits.new': '+ New trait',
  'settings.traits.units': 'Campaign units',
  'settings.traits.empty': 'No custom traits yet. Create one to add campaign-specific fields.',
  'settings.traits.edit': 'Edit',
  'settings.traits.retire': 'Retire',
  'settings.traits.in_use':
    'In use · {entities, plural, one {# entity} other {# entities}} · {facts, plural, one {# fact} other {# facts}}',
  'settings.predicates.heading': 'Custom predicates',
  'settings.predicates.help':
    'The individual fields defined by your custom traits. Predicates are authored as a trait’s fields; retiring one here removes its definition (facts persist).',
  'settings.predicates.empty': 'No custom predicates yet.',
  'settings.predicates.retire': 'Retire',
  'settings.predicates.in_use': 'In use · {facts, plural, one {# fact} other {# facts}}',
  // Usage-aware retire confirmation (settings Traits/Predicates lists +
  // inline editor delete). Shown only when the definition is still in use.
  'settings.schema.retire_heading_trait': 'Retire this trait?',
  'settings.schema.retire_heading_predicate': 'Retire this predicate?',
  'settings.schema.retire_body_trait':
    'Retiring “{label}” affects {entities, plural, one {# entity that has it attached} other {# entities that have it attached}} and {facts, plural, one {# fact} other {# facts}} written against its fields. Nothing is deleted — the data stays in the history and reappears if you re-define the trait.',
  'settings.schema.retire_body_predicate':
    'Retiring “{label}” affects {facts, plural, one {# fact} other {# facts}} written against it. Nothing is deleted — the data stays in the history and reappears if you re-define the predicate.',
  'settings.schema.retire_confirm': 'Retire anyway',
  'settings.schema.retire_cancel': 'Keep',
  // Entity types CRUD tab (03a-entities.md §4).
  'settings.types.heading': 'Custom entity types',
  'settings.types.help':
    'A type is a quick-create template — an entity root plus a default set of traits (e.g. “City” = Location with the inhabited + governed traits). Picking a type when you create an entity attaches those traits up front. A type is a suggestion, not a constraint; you can still add or remove traits afterwards.',
  'settings.types.new': '+ New type',
  'settings.types.empty': 'No custom types yet. Create one to speed up entity creation.',
  'settings.types.edit': 'Edit',
  'settings.types.retire': 'Retire',
  // Entity-type editor (inline form on the Types tab).
  'type_editor.heading': 'Define an entity type',
  'type_editor.heading_edit': 'Edit entity type',
  'type_editor.description':
    'Choose a base root and the traits this type pre-attaches on quick-create.',
  'type_editor.type_id_label': 'Type name',
  'type_editor.type_id_placeholder': 'e.g. city',
  'type_editor.label_label': 'Display label',
  'type_editor.label_placeholder': 'e.g. City (optional)',
  'type_editor.root_label': 'Base root',
  'type_editor.traits_label': 'Traits attached on create',
  'type_editor.traits_empty': 'No traits available for this root yet.',
  'type_editor.cancel': 'Cancel',
  'type_editor.save': 'Create type',
  'type_editor.save_edit': 'Save changes',
  'type_editor.error.type_id':
    'Type name must be lowercase letters, digits, and underscores, starting with a letter.',
  'fact.pending_chip': 'GM cleanup pending',
  // #182 — validation message for the `color` value kind when a manually
  // entered hex string is malformed (the color picker / hex input).
  'fact.color.invalid': 'Enter a valid hex color (#rrggbb or #rrggbbaa).',
  // #238 / B5 — the recurrence_rule authoring control (06-timeline.md §10.1).
  // Cycle / instance / label pickers are populated from the active calendar's
  // DECLARED cycles (no free text); all three AnchorSpec kinds are authorable.
  'fact.recurrence.cycle': 'Cycle',
  'fact.recurrence.cycle_placeholder': 'Choose a cycle…',
  'fact.recurrence.every_n': 'Every',
  'fact.recurrence.anchor': 'Anchor',
  'fact.recurrence.anchor_day_of_cycle': 'Day of cycle (offset)',
  'fact.recurrence.anchor_nested_position': 'Nested position (path)',
  'fact.recurrence.anchor_labeled': 'Labeled position',
  'fact.recurrence.offset': 'Offset',
  'fact.recurrence.offset_placeholder': 'e.g. 184',
  'fact.recurrence.path_cycle': 'Cycle',
  'fact.recurrence.path_instance': 'Instance',
  'fact.recurrence.instance_placeholder': 'Choose an instance…',
  'fact.recurrence.ordinal_placeholder': 'e.g. 25',
  'fact.recurrence.labeled_cycle': 'Labeled cycle',
  'fact.recurrence.label': 'Label',
  'fact.recurrence.label_placeholder': 'Choose a label…',
  'fact.recurrence.at_location': 'Location',
  'fact.recurrence.at_location_placeholder': 'No location',
  'fact.recurrence.participants': 'Participants',
  'fact.recurrence.participant_placeholder': 'Add a participant…',
  'fact.recurrence.duration_days': 'Duration (days)',
  'fact.recurrence.duration_days_placeholder': 'optional',
  'fact.recurrence.invalid_offset': 'Enter a whole-number offset for the day-of-cycle anchor.',
  'fact.recurrence.invalid_path': 'Each nested-position step needs both a cycle and an instance.',
  'fact.recurrence.invalid_label': 'Choose both a labeled cycle and one of its labels.',
  'fact.recurrence.invalid_anchor': 'Choose an anchor kind.',
  'fact.recurrence.invalid_duration': 'Duration must be a non-negative whole number of days.',
  // #202 — geometry value-kind editor (point / line / polygon). Coordinates
  // are in the parent frame's units (no global CRS); a point is a precise
  // position, a line a 1-D extent, a polygon a region. `alt_description` is
  // the a11y / map-tooltip fallback (§12).
  'fact.geometry.x': 'X',
  'fact.geometry.y': 'Y',
  'fact.geometry.point_group': 'Position (point)',
  'fact.geometry.line_group': 'Path (line)',
  'fact.geometry.polygon_group': 'Region (polygon)',
  'fact.geometry.points_label': 'Coordinates (one “x, y” per line)',
  'fact.geometry.points_placeholder': '0, 0\n10, 10\n20, 5',
  'fact.geometry.alt_label': 'Description (accessibility)',
  'fact.geometry.alt_placeholder': 'e.g. the southern third of Avara',
  'fact.geometry.invalid_point': 'Enter finite numbers for both X and Y.',
  'fact.geometry.invalid_line': 'Enter at least 2 valid “x, y” coordinate lines.',
  'fact.geometry.invalid_polygon': 'Enter at least 3 valid “x, y” coordinate lines.',
  // #203 — interior_frame editor (a Location's renderable coordinate space).
  'fact.frame.group': 'Interior frame',
  'fact.frame.width': 'Width',
  'fact.frame.height': 'Height',
  'fact.frame.units': 'Units',
  'fact.frame.units_placeholder': 'e.g. meters, feet, stylized',
  'fact.frame.origin': 'Origin',
  'fact.frame.origin_corner': 'Corner (0,0 top-left)',
  'fact.frame.origin_center': 'Center (0,0 middle)',
  'fact.frame.grid': 'Grid overlay',
  'fact.frame.grid_none': 'None',
  'fact.frame.grid_square': 'Square',
  'fact.frame.grid_hex_pointy': 'Hex (pointy-top)',
  'fact.frame.grid_hex_flat': 'Hex (flat-top)',
  'fact.frame.cell_size': 'Cell size',
  'fact.frame.background': 'Background asset id',
  'fact.frame.background_placeholder': 'optional asset id',
  'fact.frame.invalid': 'Width, height, and units are required; bounds must be positive.',
  // #206 — multi-context containment editor (parent + context + optional
  // position). A Location may carry several containment facts at once, one per
  // (parent, context); context is a free string defaulting to "geographic".
  'fact.containment.group': 'Containment',
  'fact.containment.parent': 'Parent location',
  'fact.containment.parent_placeholder': 'Choose a parent location…',
  'fact.containment.context': 'Context',
  'fact.containment.context_placeholder': 'geographic',
  'fact.containment.context_hint': 'e.g. geographic, political, cultural (defaults to geographic)',
  'fact.containment.position': 'Position',
  'fact.containment.position_none': 'No position',
  'fact.containment.position_point': 'Point',
  'fact.containment.position_line': 'Line',
  'fact.containment.position_polygon': 'Polygon',
  'fact.containment.invalid_parent': 'A parent location is required.',
  'fact.containment.invalid_position': 'The position coordinates are malformed.',
  // #219 — shared copy for the `colorPicker` design-system primitive. Used
  // by the Settings `accent_color` editor, `color`-kind fact editing, and
  // the component gallery. The swatch names are the §10.2.1 curated palette.
  'color.group': 'Color',
  'color.swatches': 'Preset colors',
  'color.custom': 'Custom color',
  'color.hex': 'Hex value',
  'color.hex_placeholder': '#rrggbb',
  'color.swatch.amber': 'Amber',
  'color.swatch.aubergine': 'Aubergine',
  'color.swatch.forest': 'Forest',
  'color.swatch.indigo': 'Indigo',
  'color.swatch.oxblood': 'Oxblood',
  'color.swatch.slate': 'Slate',
  'color.swatch.teal': 'Teal',
  // #121 — chip labels for non-default fact state. Chips render only
  // when the fact deviates from canon/canon-owner/timeless — see the
  // "only non-default" decision in `30-design-system.md` (rationale:
  // visual noise on the happy path is too high to always-render).
  'fact.status.canon': 'Canon',
  'fact.status.draft': 'Draft',
  'fact.status.hypothetical': 'Hypothetical',
  'fact.status.pending': 'Pending',
  'fact.owner.canon': 'Canon',
  'fact.owner.belief': 'Belief',
  'fact.validity.bounded': 'Time-bounded',
  // Provenance labels for the @dl/ui provenanceBadge (per
  // 30-design-system.md §3.4). GM authorship is the default; no chip
  // renders for it (the chip stack stays clean on the common case).
  'fact.author.gm': 'GM',
  'fact.author.llm': 'LLM',
  'fact.author.player': 'Player',
  'fact.author.system': 'System',
  // Calendar runtime UI (II.C.4 / #56)
  'calendar.cursor': 'Campaign date',
  'calendar.cursor_open_picker': 'Open calendar picker',
  'calendar.cursor_unset': 'No date set',
  'calendar.switch_type': 'Calendar',
  'calendar.picker_heading': 'Pick a date',
  'calendar.picker_close': 'Close',
  'calendar.era': 'Era',
  'calendar.year': 'Year',
  'calendar.month': 'Month',
  'calendar.day': 'Day',
  'calendar.clear': 'Open-ended',
  'calendar.unset': '—',
  // calendarDatePicker grid (the rich date picker) + the dateField shell
  // that wraps it. Drives the session/browse cursors, game-date facts, and
  // validity bounds.
  'calendar.grid.prev': 'Previous',
  'calendar.grid.next': 'Next',
  'calendar.grid.year_era': 'Year and era',
  'calendar.grid.days': 'Days',
  'calendar.grid.now': 'Now',
  'calendar.grid.select_year': 'Select year',
  'calendar.grid.set': 'Set',
  'calendar.grid.no_date': 'No date set',
  'calendar.grid.time': 'Time',
  'calendar.grid.intercalary': 'Intercalary',
  'calendar.grid.canonical_day': 'canonical day',
  'calendar.grid.day_n': 'Day {day}',
  'calendar.grid.backward_era_hint':
    '{era} counts backward toward the anchor; there is no year zero.',
  'calendar.grid.granularity': 'Date precision',
  'calendar.grid.granularity.day': 'Day',
  'calendar.grid.granularity.month': 'Month',
  'calendar.grid.granularity.season': 'Season',
  'calendar.grid.granularity.year': 'Year',
  'calendar.field.placeholder': 'Set date…',
  'calendar.field.trigger': 'Choose a date',
  'calendar.field.close': 'Close date picker',
  // Plugin-calendar graceful degrade (#242, 06-timeline.md §3). A plugin
  // calendar loads — its pack's entities/traits/types stay available — but
  // the structured engine cannot render it yet (the plugin runtime is
  // Phase 3), so calendar-dependent surfaces show this notice instead of a
  // date picker or formatted date. Never a crash.
  'calendar.plugin_unsupported': 'This calendar is not fully supported yet',
  'calendar.plugin_unsupported.detail':
    'It uses a plugin format that needs a future release. Its pack’s content is still available; dates show as a raw day count.',
  'validity.heading': 'Validity range',
  'validity.from': 'From',
  'validity.to': 'Until',
  'validity.none_from': 'Always',
  'validity.none_to': 'Forever',
  // Legacy keys — kept so any unmigrated call site still resolves. New
  // call sites should use the per-bound keys below.
  'validity.open_ended': 'Open-ended',
  'validity.set_open_ended': 'Set to open-ended',
  // Per-bound copy — "open-ended" on the FROM side means "always was
  // true" (no start date constraint); on the TO side it means "always
  // will be true" (no end date constraint). Surface-level distinction
  // so users don't see "no end date" on the start bound (per Image #5
  // report).
  'validity.open_ended_from': 'No start date',
  'validity.open_ended_to': 'No end date',
  'validity.set_open_ended_from': 'Set to open-ended (no start date)',
  'validity.set_open_ended_to': 'Set to open-ended (no end date)',
  // Surfaces in the calendar picker when a bound is currently open-
  // ended — the user clicks this to seed a date and switch back to
  // the picker selects.
  'validity.set_a_date': 'Set a date',
  // Precision-hint selector (ADR-0005 §5) — marks how coarsely a bound's
  // exact stored day should READ. Levels run fine → coarse (Day = exact);
  // each is a distinct word, never colour (a11y).
  'validity.from_precision': 'Start date precision',
  'validity.to_precision': 'End date precision',
  'validity.precision.day': 'Day (exact)',
  'validity.precision.month': 'Month',
  'validity.precision.season': 'Season',
  'validity.precision.year': 'Year',
  // ADR-0005 §3/§4 (#188) — valid-time segments: timeline strip + correct-vs-change.
  'segments.strip_label': 'Timeline (how this changed over in-world time)',
  'segments.bar_current': 'current',
  'segments.bar_past': 'past',
  'segments.open_start': 'since the beginning',
  'segments.open_end': 'still in effect',
  'segments.overlap_warning': 'Overlapping segments — one in-world day has two values. Resolve the dates.',
  'segments.this_changed': 'This changed…',
  'segments.this_changed_title':
    'Record that this value CHANGED as of an in-world day. The current value stays valid through the day before; the new value takes effect from the chosen day. (To fix a value that was always wrong, edit it in place instead — "Correct".)',
  'segments.correct_title':
    'Correct: edit this value in place (no new segment). Use when the value over this interval was simply wrong.',
  'segments.confirm_close':
    'Recording the change will close the current value at the day before — confirm?',
  'segments.day_label': 'Changed as of (in-world day)',
  'segments.new_value_label': 'New value',
  'segments.confirm': 'Record change',
  'segments.cancel': 'Cancel',
  // As-of scrubber (browse cursor — ADR-0005 §3, #192).
  'scrubber.label': 'Browse the world as of an in-world day',
  'scrubber.heading': 'As of',
  'scrubber.day_input': 'In-world day to browse as of',
  'scrubber.now': 'Now',
  'scrubber.return_to_now': 'Return to now',
  'scrubber.browsing_now': 'Viewing now',
  'scrubber.browsing_past': 'Browsing the past',
  // Non-color status glyphs (paired with the text above for WCAG-AA).
  'scrubber.glyph_now': '●',
  'scrubber.glyph_past': '◑',
  // Authoring gate (#192): "new at now" is disabled while diverged.
  'scrubber.author_locked':
    'Return to now to add a new value. While browsing the past you can still correct, refine, or close existing segments.',
  // III.B.1 (#65) — Player vs GM mode toggle / a11y announcement.
  'mode.chip_gm': 'GM',
  'mode.chip_player': 'Player',
  'mode.announce_gm': 'Entered as GM',
  'mode.announce_player': 'Entered as Player',
  // 226.1 (#245) — per-Viewer Preview-as picker. The GM previews exactly what
  // a selected Viewer's projection (`project(viewer, t)`) reveals at the cursor.
  'preview_as.label': 'Viewing as',
  'preview_as.gm_option': 'GM (full canon)',
  // #256 — solo facilitation mode drops the GM/Player hierarchy framing
  // (07-session-experience.md §2.1/§2.2): the lone User picks any Character to
  // view-as without the GM-ness foregrounded. The default-identity option reads
  // neutrally ("Everything") instead of "GM (full canon)".
  'preview_as.self_option': 'Everything',
  'preview_as.aria': 'Preview the campaign as a selected Viewer',
  'preview_as.banner': 'Previewing as {viewer} — only facts revealed to this Viewer are shown.',
  'preview_as.banner_icon_label': 'Preview mode',
  'preview_as.return': 'Return to GM view',
  // #256 — neutral solo-mode variant of the return control (no GM framing).
  'preview_as.return_self': 'Return to full view',
  'preview_as.panel_title': 'Revealed to {viewer}',
  'preview_as.panel_empty': 'Nothing on this entity is revealed to this Viewer yet.',
  'preview_as.computing': 'Projecting…',
  // #258 — Table View (shared-display surface). The chrome-stripped /table route
  // renders the current table focus through the designated Viewer's projection.
  'table_view.heading': 'Table View',
  'table_view.showing': 'Showing: {viewer}',
  'table_view.waiting': 'Waiting for the GM…',
  'table_view.no_viewer': 'No party Viewer set',
  'table_view.waiting_body':
    'Open this screen from the GM view. Once the GM shares the table, it appears here.',
  'table_view.no_viewer_body':
    'Add a player character to the campaign to share its view on the table.',
  'table_view.empty_body': 'Nothing is revealed to the table yet.',
  // GM-side affordances that drive the Table View.
  'table_view.open': 'Open Table View',
  'table_view.open_aria': 'Open the shared Table View in a new tab',
  'table_view.pin': 'Pin to Table',
  'table_view.pin_aria': 'Show this on the shared Table View',
  'table_view.unpin': 'Un-pin from Table',
  'table_view.unpin_aria': 'Return the Table View to the player projection',
  'table_view.designated_label': 'Table shows',
  'table_view.designated_aria': 'Choose which Viewer the shared Table View renders',
  'table_view.designated_default': 'Party (default)',
  'table_view.pinned_entity': 'Pinned to the table',
  // #271 — attention signal labels
  'table_view.attention_toggle': 'Point',
  'table_view.attention_toggle_aria': 'Toggle pointing cursor on the Table View',
  'table_view.attention_ripple': 'Look here',
  'table_view.attention_ripple_aria': 'Highlight a point on the Table View',
  'table_view.attention_pointer_label': 'GM is pointing here',
  'table_view.attention_ripple_label': 'GM is highlighting this area',
  'table_view.attention_surface_instructions':
    'Attention mode: move your cursor to point on the table. Click to highlight.',
  'table_view.attention_exit_hint': 'Pointing — press Esc or the Point button to exit',
  // III.B.2 (#66) — Player entity surfaces.
  'player.entities_empty':
    'Nothing has been revealed to you yet. As the GM shares facts, they will appear here.',
  'player.your_character': 'Your character',
  'player.your_character_hint': 'You control this character — edit its identity directly. Your changes are canon.',
  'player.entity_facts': 'What you know',
  'player.entity_no_facts': 'This entity has been mentioned to you, but no facts are revealed yet.',
  'player.status_canon': 'Canon',
  'player.validity_timeless': 'Timeless',
  // §7.3 — player-authored beliefs.
  'player.belief.heading': 'Your beliefs',
  'player.belief.hint':
    'Record what your character believes about this — it may differ from the truth. Only you and the GM can see it.',
  'player.belief.record': 'Record a belief',
  // III.B.3 (#67) — Private notes panel.
  'player.notes_heading': 'Your private notes',
  'player.notes_help': 'Only you can see these. They never leave your account.',
  'player.notes_save': 'Save',
  'player.notes_loading': 'Loading…',
  'player.notes_dirty': 'Unsaved changes',
  'player.notes_unsaved': 'Empty',
  'player.notes_saved': 'Saved',
  // A11y statement (IV.D.1 / #84) — linked from the app footer.
  'a11y.footer_link': 'Accessibility',
  'a11y.heading': 'Accessibility statement',
  'a11y.last_updated': 'Last updated: {date}',
  'a11y.conformance_heading': 'Conformance',
  'a11y.conformance_body':
    'dungeonlogs targets WCAG 2.1 Level AA across all shipped surfaces. Where naturally achievable (text contrast, structure, keyboard navigation) we exceed to AAA. Conformance is a continuous discipline: a regression in our automated a11y test gates the PR.',
  'a11y.supported_at_heading': 'Supported assistive technologies',
  'a11y.supported_at.sr_voiceover': 'VoiceOver on macOS (Safari, Chrome) and iOS (Safari).',
  'a11y.supported_at.sr_nvda': 'NVDA on Windows (Chrome, Firefox, Edge).',
  'a11y.supported_at.sr_jaws': 'JAWS on Windows (Chrome, Edge).',
  'a11y.supported_at.sr_talkback': 'TalkBack on Android (Chrome).',
  'a11y.supported_at.keyboard':
    'Full keyboard navigation — every action reachable without a pointer; command palette (⌘K / Ctrl-K) for fast navigation.',
  'a11y.supported_at.reduced_motion':
    'Respects `prefers-reduced-motion`: animations, transitions, and auto-advance behaviours collapse to instant state changes when the OS preference is set.',
  'a11y.supported_at.text_zoom':
    'Text zoom to 200% without information loss; layouts hold and tabular content scrolls horizontally rather than truncating.',
  'a11y.known_issues_heading': 'Known issues',
  'a11y.known_issues.maps':
    'Maps view (Phase 2) — the canvas view ships alongside a fully screen-readable list-alternative of pins, regions, and routes. The list-alternative is the structural answer for non-visual access; the canvas is a complement, not a replacement.',
  'a11y.known_issues.belief_layer':
    'Belief layer presentation — Phase 2 deep work refines screen-reader announcements for held-as-fact contradictions (the current pattern names the believer in the accessible name, but the multi-layer presentation needs review).',
  'a11y.contact_heading': 'Contact',
  'a11y.contact_body':
    'Found a barrier? Email accessibility@dungeonlogs.com. We aim to acknowledge within 2 business days and to ship an evaluation within 10 business days.',
  'a11y.contact_cta': 'Email the accessibility team',
  // #89 — GM recovery / backup onboarding.
  // One-time disclaimer modal (approved copy — verbatim).
  'recovery.disclaimer.title': 'Your world lives on this device',
  'recovery.disclaimer.body_1':
    'DungeonLogs keeps a full copy of your campaign in this browser and syncs it to our servers. Right now, if you lose this browser before a sync — or clear its storage — that unsynced work can’t be recovered. (Point-in-time snapshots arrive in a later release.)',
  'recovery.disclaimer.body_2':
    'Your safety net is export: on every tier, free, you can download a complete JSON + Markdown copy of any campaign at any time. Export periodically — especially before clearing browser data or switching devices — and keep that file somewhere you trust.',
  'recovery.disclaimer.body_3':
    'Deleting a campaign or your account is permanent — honored across every layer, with no undo.',
  'recovery.disclaimer.export_now': 'Export now',
  'recovery.disclaimer.got_it': 'Got it',
  'recovery.disclaimer.read_more': 'Read more about recovery',
  // /recovery page — longer plain-language explainer.
  'recovery.heading': 'Backups & recovery',
  'recovery.intro':
    'How your campaign data is stored, how to back it up, and what is permanent. The short version: export regularly — it is your safety net, free on every tier.',
  'recovery.where_heading': 'Where your data lives',
  'recovery.where_body':
    'DungeonLogs keeps a full copy of your campaign in this browser and syncs it to our servers as you work. Most of the time both copies agree. But if you lose this browser before a sync completes — or clear its storage — any work that had not yet synced cannot be recovered today. There is no server-side per-keystroke history to roll back to yet.',
  'recovery.export_heading': 'Export is your backup',
  'recovery.export_body':
    'On every tier, free, you can download a complete copy of any campaign at any time — both a full JSON snapshot and a human-readable Markdown bundle. Treat export as your real backup: run it periodically, and always before you clear browser data, switch devices, or step away from a campaign for a while. Keep the downloaded file somewhere you trust.',
  'recovery.snapshots_heading': 'What’s coming (snapshots)',
  'recovery.snapshots_body':
    'A later release adds point-in-time snapshots: durable, server-held checkpoints you can restore from without a manual export. Until that ships, export is the only recovery path — so make it a habit.',
  'recovery.deletion_heading': 'Deletion is permanent',
  'recovery.deletion_body':
    'Deleting a campaign or your account is irreversible. It is honored across every storage layer with no undo and no hidden recovery copy. If you might want the data later, export it first.',
  'recovery.back_to_app': 'Back to your campaign',
  // Settings — Data & backup section (#89).
  'settings.backup.heading': 'Data & backup',
  'settings.backup.help':
    'Export a complete JSON + Markdown copy of this campaign. Free on every tier — your safety net against lost or cleared browser data.',
  'settings.backup.last_export_label': 'Last export',
  'settings.backup.last_export_never': 'Never',
  'settings.backup.export_button': 'Export campaign',
  'settings.backup.read_more': 'Learn about backups & recovery',
  // Mobile + bottom-sheet primitives (IV.B.1 / #77).
  'mobile.bottom_sheet.close': 'Close',
  'mobile.bottom_sheet.drag_hint': 'Drag down to dismiss',
  'mobile.nav.list': 'Locations',
  'mobile.nav.drafts': 'Drafts',
  'mobile.nav.settings': 'Settings',
  // Phase 2 / 30 §7.7 — per-attribute reveal chip + Viewer-Matrix popover.
  'reveal.chip.hidden': 'Hidden — GM only. Click to reveal this fact.',
  'reveal.chip.partial': 'Revealed to specific Viewers. Click to manage.',
  // Short painted label beside the chip glyph (the verbose strings above stay
  // the tooltip / SR text). Microcopy per 30 §6 — "Reveal" / "Hidden".
  'reveal.chip.hidden_short': 'Hidden',
  'reveal.chip.partial_short': 'Revealed',
  'reveal.menu.close': 'Close reveal menu',
  'reveal.menu.visible_to': 'Visible to',
  'reveal.menu.hidden_from_all': 'Hidden from all — GM only.',
  'reveal.menu.reveal_to': 'Reveal to',
  'reveal.menu.no_viewers': 'No Viewers yet. Create a Character to reveal facts to.',
  'reveal.menu.all_revealed': 'Revealed to everyone in the campaign.',
  'reveal.menu.done': 'Done',
  // Phase 2 / 30 §7.10.2 — routine facet bulk-reveal.
  'reveal.facet.bulk_heading': 'Bulk reveal',
  'reveal.facet.chip_count': '{count} facts',
  'reveal.facet.title': 'Reveal “{facet}” · {count} facts',
  // Phase 2 / 30 §7.10.3 — Reveal Composer (sensitive facets).
  'reveal.composer.title': 'Reveal “{facet}”',
  'reveal.composer.gate':
    'This facet is sensitive. Review what becomes visible, then confirm.',
  'reveal.composer.recipient': 'Recipient',
  'reveal.composer.preview': 'Facts that will become visible',
  'reveal.composer.already_visible': 'Already visible',
  'reveal.composer.cancel': 'Cancel',
  'reveal.composer.confirm': 'Reveal {count} facts',
  // Phase 2 / 03 §9 — belief layer (author NPC beliefs / misinformation).
  'belief.section.heading': 'Beliefs',
  'belief.section.draft': 'Draft belief',
  'belief.section.empty': 'No beliefs yet. Draft what a Viewer believes about this entity.',
  'belief.row.believes': 'believes',
  'belief.row.via': '· via {source}',
  'belief.row.clone': 'Clone to…',
  'belief.row.correct': 'Mark corrected',
  'belief.row.corrected': 'Corrected',
  'belief.clone.title': 'Spread “{value}” to…',
  'belief.clone.to': 'Clone to',
  'belief.clone.none': 'No other Viewers to spread this belief to.',
  'belief.modal.title': 'Draft a belief',
  'belief.modal.help':
    'A belief is held by one Viewer and may contradict canon. Only that Viewer (and you) will see it.',
  'belief.modal.no_viewers': 'No Viewers yet. Create a Character to hold beliefs.',
  'belief.modal.believer': 'Held by',
  'belief.modal.predicate': 'About',
  'belief.modal.value': 'They believe',
  'belief.modal.cancel': 'Cancel',
  'belief.modal.confirm': 'Add belief',
  // IV.B.2 (#78) — mobile entity-page reveal + visited affordances.
  'mobile.entity.actions': 'Fact actions',
  'mobile.entity.reveal_action': 'Reveal',
  'mobile.entity.reveal_heading': 'Reveal this fact',
  'mobile.entity.reveal_help': 'Pick who can see this fact. Reveals are per-fact and can be undone.',
  'mobile.entity.reveal_pick_target': 'Pick a target Viewer',
  'mobile.entity.reveal_no_targets': 'No revealable Viewers in this campaign yet.',
  'mobile.entity.reveal_submit': 'Reveal to {viewer}',
  'mobile.entity.visited_action': 'Mark visited',
  'mobile.entity.visited_heading': 'Mark as visited',
  'mobile.entity.visited_confirm': 'Mark this Location as visited?',
  'mobile.entity.visited_submit': 'Yes, mark visited',
  'mobile.entity.notes_open': 'Open private notes',
  'mobile.entity.notes_sheet_heading': 'Private notes',
  // #81 — materialize an instance from a Lore (a creature/item template,
  // a recurring NPC archetype, …). The new entity carries an `instance_of`
  // ref back to the source Lore. Scope option (a): minimal ref only — no
  // template-trait copying (Phase 2).
  'instance.action': 'Create instance',
  'instance.sheet.heading': 'Create an instance',
  'instance.sheet.help': 'Make a new entity that points back to this Lore.',
  'instance.sheet.name_label': 'Name',
  'instance.sheet.name_placeholder': 'Instance name',
  'instance.sheet.root_label': 'Kind',
  'instance.sheet.create': 'Create instance',
  'instance.sheet.cancel': 'Cancel',
  // IV.B.3 (#79) — mobile session HUD.
  'mobile.hud.heading': 'Session cursor',
  'mobile.hud.advance': 'Advance 1 day',
  'mobile.hud.rewind': 'Rewind 1 day',
  'mobile.hud.rewind_confirm': 'Rewind cursor by 1 day?',
  'mobile.hud.rewind_yes': 'Rewind',
  'mobile.hud.rewind_no': 'Cancel',
  // Session HUD pending lane (#246 / 07-session-experience.md §8.3 /
  // 09-session-hud.md §3.5). The GM live-play surface for the #223 pending
  // lane: facts silently/surfaced-promoted to `pending` during play, with
  // inline ratify (→canon, an explicit GM gesture) and reject (→retract).
  'hud.pending.heading': 'Pending this session',
  // The attention-grabbing count badge. Only rendered when the lane mode is
  // `surface` (#223) — `silent` keeps the section + count but never notifies.
  'hud.pending.count': '{n, plural, one {# pending} other {# pending}}',
  'hud.pending.empty': 'No pending facts.',
  // Per-row provenance — pairs with the dot hue so colour is never the only
  // signal (a11y). AI marks a #223-promoted LLM draft; ratify stays GM-only.
  'hud.pending.from_llm': 'AI',
  'hud.pending.from_player': 'Player',
  'hud.pending.from_gm': 'GM',
  'hud.pending.row_label': '{entity} — {predicate}',
  'hud.pending.ratify': 'Ratify',
  'hud.pending.reject': 'Reject',
  // #265 — Rotating-facilitator HUD section (campaign home, rotating mode).
  'hud.facilitator.heading': 'Facilitator',
  'hud.facilitator.current': 'Current facilitator',
  'hud.facilitator.badge': 'Facilitator',
  'hud.facilitator.none': 'No facilitator designated',
  'hud.facilitator.set': 'Set facilitator',
  'hud.facilitator.hand_off': 'Hand off badge',
  'hud.facilitator.hand_off_title': 'Pass facilitator badge',
  'hud.facilitator.hand_off_to': 'Pass badge to {name}',
  'hud.facilitator.hand_off_confirm': 'Confirm hand-off',
  'hud.facilitator.hand_off_cancel': 'Cancel',
  'hud.facilitator.designate_title': 'Designate facilitator for this session',
  'hud.facilitator.designate_confirm': 'Designate',
  'hud.facilitator.designate_cancel': 'Cancel',
  'hud.facilitator.rotation_balance': 'Rotation balance',
  'hud.facilitator.rotation_count':
    '{count, plural, one {# session} other {# sessions}}',
  'hud.facilitator.rotation_fairness_warn':
    'Rotation is unbalanced by more than 2 sessions.',
  'hud.facilitator.you': 'You',
  'hud.facilitator.next_suggestion': 'Suggested next: {name}',
  'hud.facilitator.modal_aria': 'Designate or hand off facilitator badge',
  // Per-op version history (IV.E.1 / #87) — author-kind + status
  // annotations in the rollback chain.
  'history.entry': '{author} ({author_kind}) · {written_at} → {value}',
  'history.entry_with_status': '{author} ({author_kind}) · {written_at} · {status} → {value}',
  // Entity soft-delete (IV.E.2 / #88) — copy reserved for the eventual
  // Trash surface (Phase 2). Used today only by the substrate-level
  // payload builder's audit reason and exposed strings.
  'entity.soft_deleted': 'Entity moved to Trash',
  'entity.restored': 'Entity restored',
  // Auditor — deterministic consistency advisories (04-llm-role.md §5)
  'auditor.nav': 'Auditor',
  'mobile.nav.auditor': 'Auditor',
  'auditor.title': 'Auditor',
  'auditor.onboarding':
    'The auditor checks your canon against deterministic consistency rules. Advisories are guidance, not gates — you decide what to fix. Only blocking errors ever refuse a write.',
  'auditor.empty': 'No issues found. Your canon is consistent.',
  'auditor.empty_syncing': 'Syncing world…',
  'auditor.count': '{n, plural, one {# issue} other {# issues}}',
  'auditor.severity.blocking_error': 'Blocking',
  'auditor.severity.error': 'Error',
  'auditor.severity.warning': 'Warning',
  'auditor.severity.info': 'Info',
  'auditor.row.view_entity': 'Open the affected entity',
  // One-click suggested fixes (§5.1)
  'auditor.fix.break_cycle': 'Break the loop',
  'auditor.fix.remove_reference': 'Remove reference',
  // Rule messages — interpolate the engine-supplied params.
  'auditor.ref.dangling': '“{predicate}” points at an entity that doesn’t exist ({target}).',
  'auditor.ref.wrong_type':
    '“{predicate}” points at a {actual}, but expects {expected}.',
  'auditor.predicate.value_kind_mismatch':
    '“{predicate}” has a value that doesn’t match its type ({expected}).',
  'auditor.predicate.cardinality_violation':
    '“{predicate}” has {count} overlapping values but allows only one at a time.',
  'auditor.predicate.cardinality_violation_fuzzy':
    '“{predicate}” has {count} values whose fuzzy dates may overlap — check the bounds.',
  'auditor.belief.contradicts_canon': 'A belief about “{predicate}” contradicts canon.',
  'auditor.causal.cycle':
    '{length} events form a causal loop — each causes or precedes the next, back to the first.',
  'auditor.containment.cycle':
    'Containment loop in the “{context}” context: {path}. A place can’t contain itself — close a “valid to”, change a parent, or use a different context.',
  'auditor.recurrence.open_cycle':
    '“{entity}” recurs against the open cycle “{cycle}”. It won’t fire until that cycle’s instances close (close the open instance to resolve how often it recurs).',
  'auditor.facilitation.mode_mismatch':
    'The facilitation mode “{mode}” doesn’t match this campaign’s {member_count, plural, one {# member} other {# members}}. Change the mode or the membership to resolve.',
  'auditor.cursor_visibility.long_browse':
    'You’ve browsed past “now” for a while ({op_count, plural, one {# op} other {# ops}}) without copying anything forward. Nothing was revealed — copy a fact forward to make it canon at the current date.',
  'auditor.cursor_visibility.map_hidden':
    'The cursor visibility for “{target}” was changed in a way that contradicts an earlier reveal. Review what that viewer can see.',
  'auditor.cursor_spoof.active':
    '{spoof_count, plural, one {# character is} other {# characters are}} viewing a spoofed date. {facts_authored_during_spoof, plural, =0 {No facts have} one {# fact has} other {# facts have}} been authored under the spoof — review for consistency before ending it.',
  // Auditor rule-override settings (§5.2.1)
  'settings.auditor.heading': 'Auditor rules',
  'settings.auditor.help':
    'Tune how loudly each consistency rule speaks for this campaign. Downgrade, promote, or mute a rule to control advisory noise. (Structural-integrity rules can’t be muted.)',
  'settings.auditor.option.default': 'Default',
  'settings.auditor.option.off': 'Muted',
  'settings.auditor.rule_aria': 'Severity for the “{rule}” rule',
  'auditor.rule.ref.dangling': 'Dangling reference',
  'auditor.rule.causal.cycle': 'Causal loop',
  'auditor.rule.containment.cycle': 'Containment loop',
  'auditor.rule.ref.wrong_type': 'Wrong reference type',
  'auditor.rule.predicate.value_kind_mismatch': 'Value type mismatch',
  'auditor.rule.predicate.cardinality_violation': 'Cardinality violation',
  'auditor.rule.belief.contradicts_canon': 'Belief contradicts canon',
  'auditor.rule.recurrence.open_cycle': 'Recurrence against an open cycle',
  // Draft inbox (III.C.5 / #73)
  'inbox.open': 'Drafts',
  'inbox.title': 'Draft inbox',
  'inbox.empty': 'No drafts waiting for review.',
  'inbox.onboarding':
    'Drafts authored by the LLM (or by players in solo-facilitation mode) appear here. Ratify, edit-then-ratify, or reject — the GM is always the source of truth.',
  'inbox.ratify': 'Ratify',
  'inbox.edit': 'Edit and ratify',
  'inbox.reject': 'Reject',
  'inbox.succeeds_note': 'Ratifying also ends the prior value as of day {day}.',
  'inbox.bulk_ratify': 'Ratify selected',
  'inbox.queue_depth': '{n} draft(s) waiting',
  'inbox.provenance.llm': 'LLM draft · {provider} · {model}',
  'inbox.provenance.gm': 'GM draft',
  // Provenance-badge labels (paired with the hue + icon, never colour-only).
  'inbox.author.gm': 'GM',
  'inbox.author.llm': 'AI',
  'inbox.author.player': 'Player',
  // Draft status pill + per-card label.
  'inbox.status.draft': 'Draft',
  'inbox.card_label': '{entity} · {predicate}',
  'inbox.row_label': '{entity} — {predicate}',
  'inbox.empty_syncing': 'Syncing drafts…',
  'inbox.image_alt': 'Generated {predicate} image (draft)',
  // #251 — suggestion-set bundle review (04-llm-role.md §14). A bulk generation
  // lands as ONE reviewable unit; the GM accepts / rejects / regenerates it.
  'bundle.section_heading': 'Suggestion sets',
  'bundle.section_count': '{n} suggestion set(s) to review',
  'bundle.group_label': 'Suggestion set ({n} facts)',
  'bundle.prompt_label': 'Prompt',
  'bundle.mode.bootstrap': 'Bootstrap',
  'bundle.mode.in_world': 'In-world',
  'bundle.mode.auto': 'Auto',
  'bundle.member_count': '{n, plural, one {# fact} other {# facts}}',
  'bundle.accept_all': 'Accept all',
  'bundle.reject_all': 'Reject all',
  'bundle.accept_member': 'Accept',
  'bundle.reject_member': 'Reject',
  'bundle.regen': 'Regenerate…',
  'bundle.regen_heading': 'Regenerate this set',
  'bundle.regen_prompt_label': 'New direction — e.g. "make the NPCs more menacing"',
  'bundle.regen_submit': 'Regenerate',
  'bundle.auto_promote_label': 'Auto-promote with “needs review” flag',
  'bundle.auto_promote_hint':
    'Accept straight to canon, marked “needs review” so you can revisit later. Off by default.',
  'bundle.confidence': 'Confidence {pct}%',
  'bundle.confidence_none': 'Confidence unrated',
  // Non-colour confidence band cue (a11y: never colour-only).
  'bundle.confidence_band.high': 'High confidence',
  'bundle.confidence_band.medium': 'Medium confidence',
  'bundle.confidence_band.low': 'Low confidence',
  'bundle.confidence_band.none': 'Unrated',
  'bundle.image_alt': 'Generated {predicate} image (suggested)',
  // The GM-only "needs review" marker on an auto-promoted canon fact (§14.2).
  'bundle.review_state.unreviewed_bulk': 'Needs review',
  'bundle.review_pass': 'Mark reviewed',
  'bundle.review_pass_label': 'Mark {predicate} reviewed',
  // The two entry surfaces (decision 6).
  'bundle.entity_action': '✨ Populate with AI',
  'bundle.bootstrap_action': '✨ Generate a world',
  // The generation config + cost-confirm modal (§14.8).
  'bundle.gen_heading_entity': 'Populate with AI',
  'bundle.gen_heading_bootstrap': 'Generate a world',
  'bundle.gen_help_entity':
    'Generate a batch of grounded facts (and new entities) around this one. Everything lands as a suggestion set you review before anything becomes canon.',
  'bundle.gen_help_bootstrap':
    'Seed a region or campaign from scratch. Everything lands as a suggestion set you review before anything becomes canon.',
  'bundle.gen_prompt_label': 'What should the AI generate?',
  'bundle.gen_prompt_placeholder': 'e.g. a bustling river city ruled by a merchant council',
  'bundle.gen_depth_label': 'Depth',
  'bundle.gen_depth.sparse': 'Sparse — skeleton only',
  'bundle.gen_depth.normal': 'Normal — a rounded set',
  'bundle.gen_depth.dense': 'Dense — everything plausible',
  'bundle.gen_mode_label': 'Grounding',
  'bundle.gen_mode.auto': 'Auto (recommended)',
  'bundle.gen_mode.bootstrap': 'Bootstrap — freer rein',
  'bundle.gen_mode.in_world': 'In-world — strict consistency',
  'bundle.gen_confirm': 'Generate',
  'bundle.gen_generating': 'Generating…',
  'bundle.gen_cancel': 'Cancel',
  'bundle.gen_back': 'Back',
  // ADR-0008 §3 (T9, #283) — trait-editor entry point (settings Traits tab).
  'authoring.entry_trait': '✨ Draft a trait with AI',
  // ADR-0008 §1/§5 (T8, #282) — LLM-led interactive trait-authoring panel.
  'authoring.title': 'Define a trait with AI',
  'authoring.subtitle':
    'Describe the trait you want, then refine it turn by turn. Nothing becomes canon until you commit.',
  'authoring.close': 'Close',
  'authoring.connecting': 'Starting a session…',
  // Conversation thread.
  'authoring.thread_label': 'Authoring conversation',
  'authoring.role.you': 'You',
  'authoring.role.assistant': 'Assistant',
  'authoring.thinking': 'Thinking…',
  'authoring.empty_thread':
    'Start by describing the trait you want to model — e.g. “a corruption trait for characters and factions”.',
  // Tool "thinking" affordances (ADR-0008 §6). The glyph is a non-color cue.
  'authoring.tools_label': 'Tools used this turn',
  'authoring.tool.search_entities': '🔎 Searching entities…',
  'authoring.tool.describe_entity': '🔎 Reading an entity…',
  'authoring.tool.list_predicates': '🔎 Listing predicates…',
  'authoring.tool.find_predicate': '🔎 Searching predicates…',
  'authoring.tool.get_related': '🔎 Following relationships…',
  'authoring.tool.list_entity_images': '🔎 Looking at images…',
  'authoring.tool.propose_trait': '✎ Drafting the trait…',
  'authoring.tool.propose_predicate': '✎ Drafting a predicate…',
  'authoring.tool.generic': '⚙ Running {name}…',
  'authoring.tool_detail': '“{detail}”',
  'authoring.tool.status.started': 'in progress',
  'authoring.tool.status.succeeded': 'done',
  'authoring.tool.status.failed': 'failed',
  // Live trait artifact card.
  'authoring.artifact.heading': 'Proposed trait',
  'authoring.artifact.empty': 'The trait will take shape here as you refine it.',
  'authoring.artifact.name_label': 'Name',
  'authoring.artifact.description_label': 'Description',
  'authoring.artifact.no_description': 'No description yet.',
  'authoring.artifact.applies_to_label': 'Applies to',
  'authoring.artifact.members_label': 'Member predicates',
  'authoring.artifact.no_members': 'No member predicates yet.',
  // Diff cues — each pairs color with this non-color text label + a glyph.
  'authoring.change.added': 'Added',
  'authoring.change.removed': 'Removed',
  'authoring.change.changed': 'Changed',
  'authoring.change.field': 'Changed this turn',
  // Member predicate meta.
  'authoring.member.new': 'New',
  'authoring.member.existing': 'Existing predicate',
  'authoring.member.meta_new': '{kinds} · {cardinality} · {merge}',
  'authoring.member.enum_kind': 'enum ({options})',
  'authoring.member.ref_kind': 'reference → {roots}',
  'authoring.member.cardinality.single': 'single-valued',
  'authoring.member.cardinality.many': 'multi-valued',
  'authoring.member.merge.rebase': 'rebase',
  'authoring.member.merge.lww': 'last-write-wins',
  // Direction input.
  'authoring.direction_label': 'Your direction',
  'authoring.direction_placeholder':
    'e.g. add a severity enum, and make it apply to Factions too',
  'authoring.send': 'Send',
  'authoring.stop': 'Stop',
  // Commit.
  'authoring.commit': 'Commit trait',
  'authoring.committing': 'Committing…',
  'authoring.commit_hint':
    'Commit appends the definition to your campaign — it becomes live vocabulary immediately.',
  // Auditor advisories on a refused commit (ADR-0008 §5, pack-import mode).
  'authoring.advisories_heading': 'Before this can be committed',
  'authoring.advisories_blocked':
    'This can’t be committed yet — ask for a fix, or cancel.',
  'authoring.advisory.blocking_error': 'Blocking',
  'authoring.advisory.warning': 'Warning',
  'authoring.advisory.info': 'Note',
  // Turn / commit error.
  'authoring.error_label': 'Something went wrong',
  // LLM author-mode (✨ draft-with-AI) status + errors.
  'llm.draft_no_substrate': 'The local replica is not ready yet — try again in a moment.',
  'llm.draft_no_campaign': 'No active campaign to draft into.',
  'llm.draft_failed': 'The AI draft request failed ({status}).',
  'llm.draft_failed_generic': 'The AI draft request failed: {error}',
  'llm.draft_empty': 'The AI returned an empty draft.',
  'llm.thinking': '✨ Drafting…',
  'llm.generating': '✨ Generating {n} draft(s)…',
  'llm.stream_title': 'Drafting {predicate} · {entity}',
  'llm.stream_thinking': 'Thinking…',
  'llm.image_failed': 'The image generation request failed ({status}).',
  'llm.image_failed_generic': 'The image generation request failed: {error}',
  'llm.image_empty': 'The image generator returned nothing.',
  // Admin / LLM (III.C.8 / #92)
  'admin.llm.nav': 'Admin',
  'admin.llm.title': 'LLM Administration',
  'admin.llm.intro':
    'Configure LLM providers, models, API keys, and function bindings. Only system administrators can view this page.',
  'admin.llm.providers.title': 'Providers',
  'admin.llm.providers.enabled': 'Enabled',
  'admin.llm.providers.disabled': 'Disabled',
  'admin.llm.providers.test': 'Test connection',
  'admin.llm.providers.base_url': 'Base URL (local / OpenAI-compatible)',
  'admin.llm.providers.base_url_placeholder': 'http://localhost:11434/v1',
  'admin.llm.providers.base_url_save': 'Save URL',
  'admin.llm.providers.base_url_saved': 'Base URL saved.',
  'admin.llm.models.add_key_label': 'Model key',
  'admin.llm.models.add_key_placeholder': 'e.g. llama3.1:8b',
  'admin.llm.models.add_name_label': 'Display name (optional)',
  'admin.llm.models.add_name_placeholder': 'e.g. Llama 3.1 8B',
  'admin.llm.models.add_submit': 'Add model',
  'admin.llm.models.title': 'Models',
  'admin.llm.models.deprecated': 'Deprecated',
  'admin.llm.models.cost_per_1m': '${input} / ${output} per 1M tokens',
  'admin.llm.keys.title': 'API keys',
  'admin.llm.keys.warning':
    'API keys are encrypted at rest with AES-256-GCM. Plaintext keys are never displayed after creation — only the last 4 characters.',
  'admin.llm.keys.add': 'Add key',
  'admin.llm.keys.label': 'Label',
  'admin.llm.keys.plaintext_field': 'API key (plaintext — encrypted on submit)',
  'admin.llm.keys.last_four': '…{four}',
  'admin.llm.keys.rotate': 'Rotate',
  'admin.llm.keys.retire': 'Retire',
  'admin.llm.bindings.title': 'Function bindings',
  'admin.llm.bindings.function': 'Function',
  'admin.llm.bindings.posture': 'Posture',
  'admin.llm.bindings.model': 'Provider / model',
  'admin.llm.audit.title': 'Recent admin actions',
  'admin.llm.audit.action': 'Action',
  'admin.llm.audit.admin': 'Admin',
  'admin.llm.audit.at': 'When',
  'admin.llm.audit.target': 'Target',
  'admin.llm.audit.empty': 'No admin actions recorded yet.',
  // Workflow tab strip (Catalog · Bindings · Prompts · Analytics).
  'admin.llm.tabs.list_label': 'LLM administration sections',
  'admin.llm.tabs.catalog': 'Catalog',
  'admin.llm.tabs.bindings': 'Bindings',
  'admin.llm.tabs.prompts': 'Prompts',
  'admin.llm.tabs.limits': 'Budget & limits',
  'admin.llm.tabs.analytics': 'Analytics',
  // Budget & limits tab — cost-control policy (#216/#217/#218).
  'admin.llm.saving': 'Saving…',
  'admin.llm.limits.heading': 'Budget, rate limits & kill switch',
  'admin.llm.limits.intro':
    'Deployment-wide LLM cost controls. Every value is enforced at runtime — no redeploy needed. Campaigns may tighten these via their own settings.',
  'admin.llm.limits.saved': 'Saved.',
  'admin.llm.limits.kill.title': 'Account-wide kill switch',
  'admin.llm.limits.kill.help':
    'Instantly stop ALL LLM generation across every Account and campaign. Refused at the gateway before any provider call or spend; takes precedence over budget and rate limits. Re-enable any time.',
  'admin.llm.limits.kill.engaged': '✕ All LLM generation is DISABLED',
  'admin.llm.limits.kill.clear': '● LLM generation is enabled',
  'admin.llm.limits.kill.action_engage': 'Disable all LLM generation',
  'admin.llm.limits.kill.action_disengage': 'Re-enable LLM generation',
  'admin.llm.limits.budget.title': 'Per-Account monthly budget',
  'admin.llm.limits.budget.cap_label': 'Budget cap (USD / Account / month)',
  'admin.llm.limits.budget.cap_help':
    'The hard monthly ceiling per Account. Exceeding it returns a 402 wall and blocks further calls until the next month or a raised cap.',
  'admin.llm.limits.budget.unlimited_label': 'Allow “unlimited” budget override',
  'admin.llm.limits.budget.unlimited_help':
    'When off, no campaign may set an unlimited budget — the finite cap always applies. Leave off unless you really mean it.',
  'admin.llm.limits.budget.warn_label.0': 'First warning (% of cap)',
  'admin.llm.limits.budget.warn_label.1': 'Second warning (% of cap)',
  'admin.llm.limits.budget.warn_label.2': 'Third warning (% of cap)',
  'admin.llm.limits.budget.warn_help':
    'Soft warnings surface to the GM as spend approaches the cap. Thresholds must increase.',
  'admin.llm.limits.rate.title': 'Rate limits (per campaign)',
  'admin.llm.limits.rate.help':
    'Multi-window caps that stop runaway loops independently of the budget. The per-minute window is the primary runaway catch.',
  'admin.llm.limits.rate.per_minute': 'Calls per minute',
  'admin.llm.limits.rate.per_hour': 'Calls per hour',
  'admin.llm.limits.rate.per_day': 'Calls per day',
  // Prompts tab — version-history is a later increment; flag it inline.
  'admin.llm.prompts.history_heading': 'Version history',
  'admin.llm.prompts.history_coming':
    'Browsing, diffing, and reverting prompt-template versions is coming next.',
  // Analytics tab — trace-backed spend dashboard (#165 increment 3).
  'admin.llm.analytics.heading': 'Spend & prompt analytics',
  'admin.llm.analytics.intro':
    'Generation spend, latency, and prompt-version usage rolled up from per-call traces.',
  'admin.llm.analytics.window_label': 'Time window',
  'admin.llm.analytics.window_30d': 'Last 30 days',
  'admin.llm.analytics.window_7d': 'Last 7 days',
  'admin.llm.analytics.window_all': 'All time',
  'admin.llm.analytics.empty': 'No generations recorded in this window yet.',
  // Summary cards.
  'admin.llm.analytics.card_spend': 'Total spend',
  'admin.llm.analytics.card_traces': 'Generations',
  'admin.llm.analytics.card_latency': 'Avg latency',
  'admin.llm.analytics.card_latency_sub': 'p50 {p50}',
  'admin.llm.analytics.no_latency': 'No latency data',
  'admin.llm.analytics.ms': '{n} ms',
  // Spend by function.
  'admin.llm.analytics.by_function_heading': 'Spend by function',
  'admin.llm.analytics.col_function': 'Function',
  'admin.llm.analytics.col_traces': 'Generations',
  'admin.llm.analytics.col_cost': 'Cost',
  // Spend by model.
  'admin.llm.analytics.by_model_heading': 'Spend by model',
  'admin.llm.analytics.col_model': 'Model',
  'admin.llm.analytics.col_calls': 'Calls',
  'admin.llm.analytics.col_tokens': 'Tokens (in / out)',
  'admin.llm.analytics.tokens': '{input} / {output}',
  // Usage by prompt version.
  'admin.llm.analytics.by_prompt_heading': 'Usage by prompt version',
  'admin.llm.analytics.col_prompt': 'Prompt template',
  'admin.llm.analytics.col_version': 'Version',
  'admin.llm.analytics.col_uses': 'Uses',
  // Tool calls.
  'admin.llm.analytics.tools_heading': 'Tool-call frequency',
  'admin.llm.analytics.col_tool': 'Tool',
  'admin.llm.analytics.tools_empty': 'No tool calls in this window.',
  // Acceptance rate — deferred (no draft-outcome→trace link in the substrate).
  'admin.llm.analytics.acceptance_heading': 'Acceptance rate by prompt version',
  'admin.llm.analytics.acceptance_untracked':
    'Not yet tracked. Linking draft outcomes (ratify / edit / reject) to the originating prompt version requires a draft-outcome join that the trace substrate does not record yet.',
  // Shared loading / error / empty / not-found surfaces for admin panels.
  'admin.llm.loading': 'Loading…',
  'admin.llm.error.generic': 'Could not load. Try again in a moment.',
  'admin.llm.error.retry': 'Retry',
  'admin.llm.not_found.heading': 'Not found',
  'admin.llm.not_found.body':
    'This page does not exist, or you do not have permission to view it.',
  'admin.llm.unauthenticated.heading': 'Sign in required',
  'admin.llm.unauthenticated.body':
    'Sign in to continue.',
  'admin.llm.unauthenticated.link': 'Go to sign in',
  'admin.llm.providers.empty': 'No providers configured.',
  'admin.llm.providers.models_count': '{n} model(s)',
  'admin.llm.providers.keys_count': '{n} active key(s)',
  'admin.llm.providers.adapter_missing': 'Adapter not registered',
  'admin.llm.providers.last_rotated': 'Last rotated {at}',
  'admin.llm.providers.never_rotated': 'No rotation yet',
  'admin.llm.providers.test_pending': 'Testing…',
  'admin.llm.providers.test_ok': 'Connection ok',
  'admin.llm.providers.test_error': 'Test failed: {error}',
  'admin.llm.providers.toggle_enable': 'Enable {name}',
  'admin.llm.providers.toggle_disable': 'Disable {name}',
  'admin.llm.providers.test_label': 'Test connection for {name}',
  'admin.llm.providers.select_label': 'Select provider',
  'admin.llm.models.empty': 'This provider has no models.',
  'admin.llm.models.select_provider': 'Select a provider above to view its models.',
  'admin.llm.keys.empty': 'No keys for this provider.',
  'admin.llm.keys.select_provider': 'Select a provider above to view its keys.',
  'admin.llm.keys.created_at': 'Added {at}',
  'admin.llm.keys.retired_at': 'Retired {at}',
  'admin.llm.keys.active_badge': 'Active',
  'admin.llm.keys.retired_badge': 'Retired',
  'admin.llm.keys.rotate_label': 'Rotate key {label}',
  'admin.llm.keys.retire_label': 'Retire key {label}',
  'admin.llm.keys.add_heading': 'Add a new key',
  'admin.llm.keys.label_placeholder': 'e.g. production',
  'admin.llm.keys.submit': 'Add key',
  'admin.llm.keys.error_label_required': 'Label is required.',
  'admin.llm.keys.error_plaintext_required':
    'API key is required (minimum 8 characters).',
  'admin.llm.keys.added_confirmation':
    'Key added. The plaintext is not displayed — only the last 4 characters: {four}.',
  'admin.llm.bindings.empty': 'No function bindings configured.',
  'admin.llm.bindings.cell_label': '{function} / {posture}',
  'admin.llm.bindings.saved': 'Saved.',
  'admin.llm.bindings.save_error': 'Could not save: {error}',
  // Generation settings — per-binding knobs (standard posture).
  'admin.llm.gen.title': 'Generation settings',
  'admin.llm.gen.body':
    'Tune how the bound model generates. Applies to the standard posture binding.',
  'admin.llm.gen.no_binding':
    'Bind a provider for this function in the matrix above to configure its settings.',
  'admin.llm.gen.saved': 'Saved.',
  'admin.llm.gen.save_error': 'Could not save: {error}',
  'admin.llm.gen.reasoning_label': 'Reasoning effort (text drafting)',
  'admin.llm.gen.reasoning_help':
    'Lower is faster. “None” makes reasoning models answer directly instead of thinking for tens of seconds. Non-reasoning models ignore this.',
  'admin.llm.gen.reasoning_default': 'Model default',
  'admin.llm.gen.reasoning_none': 'None (fastest)',
  'admin.llm.gen.reasoning_low': 'Low',
  'admin.llm.gen.reasoning_medium': 'Medium',
  'admin.llm.gen.reasoning_high': 'High',
  'admin.llm.gen.size_label': 'Image size',
  'admin.llm.gen.size_default': 'Provider default (512×512)',
  'admin.llm.gen.steps_label': 'Sampler steps',
  'admin.llm.gen.steps_help':
    'Local diffusion servers only (Draw Things / Automatic1111). Higher = slower, more detail. Blank uses the default (20).',
  'admin.llm.gen.steps_placeholder': '20',
  // Prompt templates (admin-managed global defaults).
  'admin.llm.templates.title': 'Prompt templates',
  'admin.llm.templates.body':
    'The system + user prompts for each AI task. Edits override the built-in default for every campaign; per-campaign additions are set by GMs in campaign AI settings.',
  'admin.llm.templates.placeholders': 'Placeholders: {tokens}',
  'admin.llm.templates.missing_required':
    '⚠ Missing required placeholder(s): {tokens} — the AI task needs these to work.',
  'admin.llm.templates.edited_badge': 'Edited',
  'admin.llm.templates.save': 'Save',
  'admin.llm.templates.reset': 'Reset to default',
  'admin.llm.templates.save_error': 'Could not save: {error}',
  // Prompt-template version history — browse / diff / revert (#165).
  'admin.llm.versions.heading': 'Version history',
  'admin.llm.versions.body':
    'Every template edit appends a version. Browse past versions, diff them against the live one, and revert — reverting appends a new version, it never rewrites history.',
  'admin.llm.versions.template_label': 'Template',
  'admin.llm.versions.list_label': 'Versions',
  'admin.llm.versions.detail_label': 'Selected version',
  'admin.llm.versions.empty': 'No saved versions yet — this template is still the built-in default.',
  'admin.llm.versions.version_n': 'Version {version}',
  'admin.llm.versions.author': 'by {author}',
  'admin.llm.versions.current_badge': '✓ Current',
  'admin.llm.versions.detail_heading': 'Version {version}',
  'admin.llm.versions.body_label': 'Body',
  'admin.llm.versions.diff_heading': 'Changes vs. current (version {version})',
  'admin.llm.versions.diff_heading_default': 'Changes vs. the built-in default',
  'admin.llm.versions.diff_label': 'Line-level diff; + marks added lines, − marks removed lines',
  'admin.llm.versions.diff_identical': 'Identical to the current version.',
  'admin.llm.versions.revert': 'Revert to version {version}',
  'admin.llm.versions.revert_confirm': 'Confirm revert',
  'admin.llm.versions.revert_cancel': 'Cancel',
  'admin.llm.versions.revert_error': 'Could not revert: {error}',
  // Admin / signup allowlist (invite-only sign-ups).
  'admin.signups.nav': 'Sign-ups',
  'admin.signups.title': 'Sign-up Allowlist',
  'admin.signups.intro':
    'Restrict who can create an account. When enabled, only emails on the allowlist may sign up. Off by default. Only system administrators can view this page.',
  'admin.signups.mode.title': 'Invite-only mode',
  'admin.signups.mode.help':
    'When enabled, sign-ups are restricted to the emails on the allowlist below. When disabled, anyone can sign up.',
  'admin.signups.mode.label': 'Require an invite to sign up',
  'admin.signups.mode.toggle_on': 'Enable invite-only sign-ups',
  'admin.signups.mode.toggle_off': 'Disable invite-only sign-ups',
  'admin.signups.allowlist.title': 'Allowed emails',
  'admin.signups.allowlist.email_label': 'Email',
  'admin.signups.allowlist.note_label': 'Note (optional)',
  'admin.signups.allowlist.add': 'Add to allowlist',
  'admin.signups.allowlist.remove': 'Remove',
  'admin.signups.allowlist.remove_label': 'Remove {email} from the allowlist',
  'admin.signups.allowlist.empty': 'No emails on the allowlist yet.',
  'admin.signups.allowlist.email_required': 'Enter an email address.',
  'admin.signups.allowlist.duplicate': 'That email is already on the allowlist.',
  'admin.signups.error.generic': 'Could not load. Try again in a moment.',
  'admin.signups.not_found.heading': 'Not found',
  'admin.signups.not_found.body':
    'This page does not exist, or you do not have permission to view it.',
  'admin.signups.unauthenticated.heading': 'Sign in required',
  'admin.signups.unauthenticated.body': 'Sign in to continue.',
  'admin.signups.unauthenticated.link': 'Go to sign in',
  // Account menu (header avatar dropdown).
  'account.menu.label': 'Account menu for {email}',
  'account.menu.close': 'Close account menu',
  'account.menu.signed_in_as': 'Signed in as',
  'account.menu.account': 'Account settings',
  // Campaign settings — GM-only entry; moved here from the topbar in the
  // chrome restructure (topbar → slim topbar + left rail).
  'account.menu.settings': 'Campaign settings',
  'account.menu.admin': 'Admin',
  // #152 — account / profile settings page.
  'account.page.title': 'Account settings',
  'account.loading': 'Loading your account…',
  'account.error': 'Could not load your account. Try again.',
  'common.cancel': 'Cancel',
  'common.dismiss': 'Dismiss',
  // Profile card.
  'account.profile.title': 'Profile',
  'account.profile.email': 'Email',
  'account.profile.verification': 'Email status',
  'account.profile.verified': 'Verified',
  'account.profile.unverified': 'Not verified',
  'account.profile.member_since': 'Member since',
  'account.profile.user_id': 'Account ID',
  // Security card — 2FA.
  'account.security.title': 'Security',
  'account.security.twofa.label': 'Two-factor authentication',
  'account.security.twofa.on': 'Enabled',
  'account.security.twofa.off': 'Disabled',
  'account.security.twofa.help':
    'Add a time-based one-time code (TOTP) from an authenticator app as a second step at sign-in.',
  'account.security.twofa.enable': 'Enable two-factor',
  'account.security.twofa.disable': 'Disable two-factor',
  'account.security.twofa.setup_help':
    'Add this setup key to your authenticator app, then enter the 6-digit code it shows to confirm.',
  'account.security.twofa.secret': 'Setup key',
  'account.security.twofa.code_label': 'Authentication code',
  'account.security.twofa.confirm': 'Confirm & enable',
  'account.security.twofa.backup_help':
    'Save these backup codes somewhere safe. Each one works once if you lose your authenticator. They will not be shown again.',
  'account.security.twofa.done': 'I have saved my codes',
  'account.security.twofa.password_prompt':
    'Confirm your password to disable two-factor authentication.',
  'account.security.twofa.disable_sent':
    'Check your email — we sent a link to finish disabling two-factor authentication.',
  // Security card — password.
  'account.security.password.label': 'Password',
  'account.security.password.help':
    'For your security, password changes happen by email. Send yourself a reset link to choose a new password.',
  'account.security.password.send_reset': 'Send password reset email',
  'account.security.password.reset_sent':
    'If your email is on file, a password reset link is on its way.',
  // Recovery email (#152 follow-up — moved off sign-up).
  'account.security.recovery.label': 'Recovery email',
  'account.security.recovery.help':
    'A separate inbox that password-reset emails are sent to. Useful if you ever lose access to your primary email. Leave blank to use your primary email.',
  'account.security.recovery.placeholder': 'you@example.com',
  'account.security.recovery.save': 'Save recovery email',
  'account.security.recovery.saved': 'Recovery email updated.',
  'account.security.recovery.error_same':
    'Recovery email must be different from your primary email.',
  // #F8 — re-authentication before repointing the recovery mailbox.
  'account.security.recovery.password_label': 'Current password',
  'account.security.recovery.password_help':
    'Confirm your password to change where password-reset emails are sent.',
  'account.security.recovery.error_password_required':
    'Enter your current password to change your recovery email.',
  // Danger zone.
  'account.danger.title': 'Danger zone',
  'account.danger.help':
    'Deleting your account is irrevocable after a 30-day grace period. All your campaigns and data are removed.',
  'account.danger.confirm_label': 'Type {phrase} to confirm',
  'account.danger.delete': 'Delete my account',
  // Admin sidebar (groups every system-admin surface behind one nav).
  'admin.nav.label': 'Admin sections',
  'admin.nav.title': 'Administration',
  'admin.nav.users': 'Users',
  'admin.nav.llm': 'LLM',
  'admin.nav.signups': 'Sign-ups',
  'admin.nav.audit': 'Audit log',
  'admin.nav.back': 'Back to app',
  // Admin users directory.
  'admin.users.title': 'Users',
  'admin.users.intro':
    'Every account on this deployment. Click a row for full detail. Secrets are never shown.',
  'admin.users.loading': 'Loading users…',
  'admin.users.count': '{n, plural, one {# user} other {# users}}',
  'admin.users.count_range': '{from}–{to} of {total, plural, one {# user} other {# users}}',
  'admin.users.prev': 'Previous',
  'admin.users.next': 'Next',
  // Admin user actions (detail panel).
  'admin.users.actions.title': 'Actions',
  'admin.users.actions.working': 'Working…',
  'admin.users.actions.done': 'Done.',
  'admin.users.actions.email_sent': 'Email sent.',
  'admin.users.actions.promote': 'Make admin',
  'admin.users.actions.demote': 'Remove admin',
  'admin.users.actions.promote_confirm': 'Grant system-admin to {email}?',
  'admin.users.actions.demote_confirm': 'Remove system-admin from {email}?',
  'admin.users.actions.revoke': 'Sign out everywhere',
  'admin.users.actions.revoke_confirm': 'Revoke all active sessions for {email}?',
  'admin.users.actions.revoked':
    '{n, plural, =0 {No active sessions.} one {Revoked # session.} other {Revoked # sessions.}}',
  'admin.users.actions.password_reset': 'Send password reset',
  'admin.users.actions.resend_verification': 'Resend verification',
  'admin.users.actions.cancel_deletion': 'Cancel deletion',
  'admin.users.actions.cancel_deletion_confirm': 'Cancel the pending deletion for {email}?',
  'admin.users.empty': 'No users match your search.',
  'admin.users.search_label': 'Filter users',
  'admin.users.search_placeholder': 'Search by email, account, or id…',
  'admin.users.select_prompt': 'Select a user to see full detail.',
  'admin.users.yes': 'Yes',
  'admin.users.no': 'No',
  'admin.users.col.email': 'User',
  'admin.users.col.status': 'Status',
  'admin.users.col.campaigns': 'Campaigns',
  'admin.users.col.sessions': 'Active sessions',
  'admin.users.col.created': 'Joined',
  'admin.users.badge.admin': 'Admin',
  'admin.users.badge.member': 'Member',
  'admin.users.badge.verified': 'Verified',
  'admin.users.badge.unverified': 'Unverified',
  'admin.users.badge.twofa': '2FA',
  'admin.users.badge.pending_deletion': 'Pending deletion',
  'admin.users.detail.loading': 'Loading detail…',
  'admin.users.detail.id': 'User id',
  'admin.users.detail.account': 'Account',
  'admin.users.detail.account_id': 'Account id',
  'admin.users.detail.account_none': 'No account',
  'admin.users.detail.created': 'Joined',
  'admin.users.detail.verified': 'Email verified',
  'admin.users.detail.recovery_email': 'Recovery email',
  'admin.users.detail.recovery_none': 'None set',
  'admin.users.detail.twofa': 'Two-factor auth',
  'admin.users.detail.twofa_on': 'Enabled {at}',
  'admin.users.detail.backup_codes': 'Backup codes remaining',
  'admin.users.detail.pending_deletion': 'Pending deletion since',
  'admin.users.detail.memberships': 'Campaign memberships',
  'admin.users.detail.memberships_empty': 'Not a member of any campaign.',
  'admin.users.detail.owned': 'Owned campaigns',
  'admin.users.detail.owned_empty': 'Owns no campaigns.',
  'admin.users.detail.sessions': 'Sessions',
  'admin.users.detail.sessions_empty': 'No sessions on record.',
  'admin.users.detail.session_active': 'Active',
  'admin.users.detail.session_revoked': 'Revoked',
  'admin.users.detail.session_expired': 'Expired',
  'admin.users.detail.session_seen': 'Last seen {at}',
  'admin.users.detail.session_unknown_agent': 'Unknown device',
  'admin.users.detail.role_gm': 'GM',
  'admin.users.detail.role_player': 'Player',
  'admin.users.detail.emails': 'Recent emails',
  'admin.users.detail.emails_empty': 'No emails sent.',
  'admin.users.error.generic': 'Could not load. Try again in a moment.',
  'admin.users.not_found.heading': 'Not found',
  'admin.users.not_found.body':
    'This page does not exist, or you do not have permission to view it.',
  'admin.users.unauthenticated.heading': 'Sign in required',
  'admin.users.unauthenticated.body': 'Sign in to continue.',
  'admin.users.unauthenticated.link': 'Go to sign in',
  // Admin audit log.
  'admin.audit.title': 'Audit log',
  'admin.audit.intro':
    'Every administrative change — LLM config, sign-up gate, and user management — most recent first.',
  'admin.audit.loading': 'Loading audit log…',
  'admin.audit.empty': 'No administrative actions recorded yet.',
  'admin.audit.error.generic': 'Could not load. Try again in a moment.',
  'admin.audit.col.when': 'When',
  'admin.audit.col.action': 'Action',
  'admin.audit.col.admin': 'Admin',
  'admin.audit.col.target': 'Target',
  'admin.audit.col.details': 'Details',
  'admin.audit.not_found.heading': 'Not found',
  'admin.audit.not_found.body':
    'This page does not exist, or you do not have permission to view it.',
  'admin.audit.unauthenticated.heading': 'Sign in required',
  'admin.audit.unauthenticated.body': 'Sign in to continue.',
  'admin.audit.unauthenticated.link': 'Go to sign in',
  // Auth UI (#117) — sign-in / sign-up / verify / reset / sign-out.
  'auth.sign_in.heading': 'Sign in',
  'auth.sign_in.subtitle': 'Welcome back. Pick up where your world left off.',
  'auth.sign_in.email_label': 'Email',
  'auth.sign_in.password_label': 'Password',
  'auth.sign_in.remember_label': 'Keep me signed in on this device',
  'auth.sign_in.submit': 'Sign in',
  'auth.sign_in.forgot_link': 'Forgot your password?',
  'auth.sign_in.no_account': 'No account yet?',
  'auth.sign_in.sign_up_link': 'Create one',
  'auth.sign_in.totp_label': 'Authenticator code',
  'auth.sign_in.totp_help':
    'Enter the 6-digit code from your authenticator app. Backup codes work here too.',
  'auth.sign_in.error_invalid': 'Email or password not recognised. Try again.',
  'auth.sign_in.error_unverified':
    'Your email is not verified yet. Check your inbox for the confirmation link.',
  'auth.sign_in.error_locked':
    'Too many failed attempts. Wait a few minutes and try again.',
  'auth.sign_in.error_totp': 'That authenticator code is not valid.',
  'auth.sign_up.heading': 'Create your account',
  'auth.sign_up.subtitle': 'Start building a world your players can step into.',
  'auth.sign_up.email_label': 'Email',
  'auth.sign_up.password_label': 'Password',
  'auth.sign_up.password_help': 'At least 12 characters. Use a passphrase — easier to remember, harder to crack.',
  'auth.sign_up.tos_label': 'I agree to the Terms of Service and Privacy Policy.',
  'auth.sign_up.submit': 'Create account',
  'auth.sign_up.have_account': 'Already have an account?',
  'auth.sign_up.sign_in_link': 'Sign in',
  'auth.sign_up.error_short': 'Password must be at least 12 characters.',
  'auth.sign_up.error_exists':
    'An account with that email already exists. Try signing in.',
  'auth.sign_up.error_invite_only':
    'Sign-ups are invite-only. Contact the administrator.',
  'auth.sign_up.success_heading': 'Almost done',
  'auth.sign_up.success_body':
    'We sent a confirmation link to {email}. Click it to finish creating your account.',
  'auth.verify.heading': 'Verifying your email',
  'auth.verify.pending': 'Confirming your email…',
  'auth.verify.success': 'Your email is verified. You can sign in now.',
  'auth.verify.continue_link': 'Continue to sign in',
  'auth.verify.error': 'The confirmation link is invalid or has expired.',
  'auth.verify.request_new_link': 'Request a new sign-up link',
  'auth.twofa_disable.heading': 'Disabling two-factor authentication',
  'auth.twofa_disable.pending': 'Confirming…',
  'auth.twofa_disable.success':
    'Two-factor authentication has been disabled. You can now sign in with just your password.',
  'auth.twofa_disable.continue_link': 'Continue to sign in',
  'auth.twofa_disable.error': 'This link is invalid or has expired.',
  'auth.twofa_disable.request_new_link': 'Back to sign in',
  'auth.forgot.heading': 'Reset your password',
  'auth.forgot.body':
    'Enter your account email. If we know it, we will send a reset link to your inbox.',
  'auth.forgot.email_label': 'Email',
  'auth.forgot.submit': 'Send reset link',
  'auth.forgot.sent':
    'If the email is registered, a reset link is on its way. Check your inbox (and spam folder).',
  'auth.forgot.back_link': 'Back to sign in',
  'auth.reset.heading': 'Choose a new password',
  'auth.reset.password_label': 'New password',
  'auth.reset.confirm_label': 'Confirm new password',
  'auth.reset.submit': 'Update password',
  'auth.reset.error_mismatch': 'The two passwords do not match.',
  'auth.reset.error_short': 'Password must be at least 12 characters.',
  'auth.reset.error_invalid_token':
    'This reset link is invalid or has expired. Request a new one.',
  'auth.reset.success': 'Password updated. You can sign in with your new password now.',
  'auth.reset.continue_link': 'Continue to sign in',
  'auth.sign_out': 'Sign out',
  'auth.signed_out_toast': 'Signed out.',
  // Campaign creation / picker / switcher.
  'campaigns.title': 'Your campaigns',
  'campaigns.intro':
    'A campaign is the sovereign container for one world and its play. Pick one to enter, or create another.',
  'campaigns.create_first.heading': 'Create your first campaign',
  'campaigns.create_first.intro':
    'A campaign holds your worldbuilding work. Give it a name and pick a facilitation style — both can change later.',
  'campaigns.create.heading': 'Create a campaign',
  'campaigns.create.name_label': 'Campaign name',
  'campaigns.create.name_placeholder': 'e.g. The Dragonsworn Chronicles',
  'campaigns.create.facilitation_label': 'Facilitation style',
  'campaigns.create.facilitation_help':
    'GM-led: one game master runs the table. Solo: you play and facilitate. Other modes arrive in a later phase.',
  'campaigns.facilitation.gm': 'GM-led',
  'campaigns.facilitation.solo': 'Solo / journal mode',
  'campaigns.facilitation.rotating': 'Rotating facilitator',
  'campaigns.facilitation.flat': 'Flat / GMless',
  'campaigns.create.submit': 'Create campaign',
  'campaigns.create.submitting': 'Creating…',
  'campaigns.create.error_name_required': 'Campaign name is required.',
  'campaigns.create.error_generic':
    'Could not create the campaign. Try again in a moment.',
  'campaigns.picker.enter': 'Enter',
  'campaigns.picker.create_another': 'Create another campaign',
  'campaigns.picker.last_active': 'Last active {at}',
  'campaigns.picker.never_active': 'No recent activity on this device',
  'campaigns.switcher.label': 'Switch campaign',
  'campaigns.switcher.current': 'Current campaign: {name}',
  'campaigns.switcher.heading': 'Campaigns',
  'campaigns.switcher.count': '{count, plural, one {# campaign} other {# campaigns}}',
  'campaigns.switcher.manage': 'Manage all campaigns',
  'campaigns.switcher.create': 'Create new campaign',
  'campaigns.switcher.only_one_hint':
    'This is your only campaign. Create another to keep separate worlds, tables, or systems apart.',
  'campaigns.switcher.close': 'Close campaign switcher',
  'campaigns.boot.loading': 'Loading your campaigns…',
  'campaigns.boot.error':
    'Could not load your campaigns. Sign out and try again, or refresh the page.',
  // Generic entity creation (#131).
  'entity.new.button': 'New entity',
  'entity.new.picker_heading': 'What kind of entity?',
  'entity.new.picker_help':
    'Locations, characters, factions, events, items, and lore all share the same fact-stream substrate. Pick a starting root — you can always change traits later.',
  'entity.new.types_heading': 'From a custom type',
  'entity.new.location': 'New Location',
  'entity.new.character': 'New Character',
  'entity.new.faction': 'New Faction',
  'entity.new.event': 'New Event',
  'entity.new.item': 'New Item',
  'entity.new.lore': 'New Lore',
  'entity.root.location': 'Location',
  'entity.root.character': 'Character',
  'entity.root.faction': 'Faction',
  'entity.root.event': 'Event',
  'entity.root.item': 'Item',
  'entity.root.lore': 'Lore',
  // Bug 4 — new-entity name modal strings.
  'new_entity.modal.name_label': 'Name',
  'new_entity.modal.name_placeholder': '{root} name',
  'new_entity.modal.save': 'Create',
  'new_entity.modal.cancel': 'Cancel',
  'palette.create_for_root': 'Create {root}: "{query}"',
  'palette.filter_chip_type': 'Filter: {root}',
  'palette.filter_chip_is': 'Filter: has {predicate}',
  'palette.filter_chip_is_not': 'Filter: lacks {predicate}',
  'entities.list.region_label': 'Entities by root',
  'entities.list.count_label':
    '{root}: {n, plural, =0 {no entities} one {# entity} other {# entities}}',
  'entities.empty.location':
    'No locations yet. Create one to start mapping the world.',
  'entities.empty.character':
    'No characters yet. Create one to track who inhabits the world.',
  'entities.empty.faction':
    'No factions yet. Create one to track guilds, courts, and rival powers.',
  'entities.empty.event':
    'No events yet. Create one to anchor moments on the timeline.',
  'entities.empty.item':
    'No items yet. Create one to track artefacts, gear, and loot.',
  'entities.empty.lore':
    'No lore yet. Create one for gods, languages, schools of magic, and other abstractions.',
  // Trait attach / detach UI (#139). Trait labels + descriptions are
  // declared per trait id in `core.json`; UI strings under
  // `entity.traits.*` cover the chip strip, picker dialog, and
  // detach-confirm prompt.
  'entity.traits.region_label': 'Attached traits',
  'entity.traits.attach_button': '+ Attach trait',
  'entity.traits.remove_label': 'Detach the "{trait}" trait',
  // #143 — trait picker affordance copy. Each row carries an explicit
  // "Attach" action so the picker reads as a list of actionable rows,
  // not an info panel. The aria-label adds the trait name for SR users.
  'entity.traits.row_attach': 'Attach',
  'entity.traits.row_attach_label': 'Attach the "{trait}" trait',
  'entity.traits.picker_heading': 'Attach a trait',
  'entity.traits.picker_help':
    'Traits bundle related facts (a "mortal" character carries "born in", "died in", "cause of death"; an "inhabited" location carries "population", "government"). Each trait attaches at most once per entity — attaching the same trait twice would only duplicate its predicate list. Attach what fits — you can detach later without losing data.',
  'entity.traits.picker_empty':
    'Every trait that applies to this entity is already attached.',
  'entity.traits.picker_search_placeholder': 'Search traits…',
  'entity.traits.picker_search_label': 'Filter traits by name or description',
  'entity.traits.picker_source':
    'Traits come from installed packs (the list above is the bundled "core" pack) plus any you define for this campaign.',
  // Trait editor (#73) — the "Define a new trait" affordance now opens a
  // real editor that writes a campaign-local homebrew trait.
  'entity.traits.picker_define': 'Define a new trait',
  'entity.traits.detach_heading': 'Detach this trait?',
  'entity.traits.detach_body':
    'Detaching "{trait}" will hide {n, plural, one {# fact} other {# facts}} from this entity. The data is preserved in the history and reappears if you re-attach the trait.',
  'entity.traits.detach_cancel': 'Keep attached',
  'entity.traits.detach_confirm': 'Detach trait',
  // Trait editor (#73 / ADR-0001).
  'trait_editor.heading': 'Define a trait',
  'trait_editor.heading_edit': 'Edit trait',
  'trait_editor.save_edit': 'Save changes',
  'trait_editor.edit_trait': 'Edit the "{trait}" trait',
  'trait_editor.kind_locked_hint':
    'Type is locked for existing fields — changing it would re-interpret saved values.',
  'trait_editor.error.kind_locked':
    'Field "{id}" already has saved data — its type can\'t change. Add a new field instead.',
  'trait_editor.error.cardinality_narrow':
    'Field "{id}" can\'t go from many values to one without dropping data. Leave it as many.',
  'trait_editor.allow_risky': 'Allow type changes to existing fields',
  'trait_editor.risky_warning':
    'Risky: changing an existing field\'s type or narrowing it to one value won\'t convert data already saved on it. Those values stay in the history as written and may not match the new shape.',
  'trait_editor.description':
    'A trait bundles fields (predicates) that compose onto an entity. Choose which entity types it applies to, then add its fields.',
  'trait_editor.applies_to_label': 'Applies to',
  'trait_editor.trait_id_label': 'Trait name',
  'trait_editor.trait_id_placeholder': 'e.g. haunted',
  'trait_editor.fields_heading': 'Fields',
  'trait_editor.field_id_label': 'Field name',
  'trait_editor.field_unresolved': '⚠ Unresolved field (predicate missing — remove it)',
  'trait_editor.field_id_placeholder': 'e.g. haunting_intensity',
  'trait_editor.field_kind_label': 'Type',
  'trait_editor.field_cardinality_label': 'Allows',
  'trait_editor.dimension_label': 'Unit dimension',
  'trait_editor.dimension_none': 'No unit',
  'trait_editor.boolean_true_label': 'Label for “on”',
  'trait_editor.boolean_true_placeholder': 'On label (e.g. Walled)',
  'trait_editor.boolean_false_label': 'Label for “off”',
  'trait_editor.boolean_false_placeholder': 'Off label (e.g. Open)',
  'trait_editor.cardinality_single': 'One value',
  'trait_editor.cardinality_many': 'Many values',
  'trait_editor.add_field': 'Add a field',
  'trait_editor.remove_field': 'Remove field',
  'trait_editor.save': 'Create trait',
  'trait_editor.cancel': 'Cancel',
  'trait_editor.delete': 'Delete trait',
  'trait_editor.kind.text': 'Text',
  'trait_editor.kind.rich_text': 'Long text',
  'trait_editor.kind.number': 'Number',
  'trait_editor.kind.boolean': 'Yes / No',
  'trait_editor.kind.enum': 'Choice list',
  'trait_editor.kind.ref': 'Link to entity',
  'trait_editor.enum_values_label': 'Choices',
  'trait_editor.enum_values_placeholder': 'Comma-separated, e.g. low, medium, high',
  'trait_editor.ref_root_label': 'Restrict to',
  'trait_editor.ref_any': 'Any entity',
  'trait_editor.reciprocal_label': 'Reciprocal (reads as on the target)',
  'trait_editor.reciprocal_placeholder': 'Reciprocal label, e.g. held by (optional)',
  'trait_editor.error.enum_values':
    'Field "{id}" is a choice list — add at least one comma-separated choice.',
  'trait_editor.error.trait_id':
    'Trait name must be lowercase letters, digits, and underscores, starting with a letter.',
  'trait_editor.error.no_fields': 'Add at least one field.',
  'trait_editor.error.field_id':
    'Field name "{id}" must be lowercase letters, digits, and underscores, starting with a letter.',
  'trait_editor.error.dup_field': 'Field "{id}" is listed more than once.',
  'trait_editor.error.no_roots': 'Pick at least one entity type this trait applies to.',
  // --- ADR-0002 value-kinds, tuples, campaign units, uploads (session additions) ---
  'entity.facts.upload': 'Upload',
  'entity.facts.upload_failed': 'Upload failed. Please try again.',
  'entity.unit.none': 'No unit',
  'trait_editor.error.tuple_field_dup': 'Field "{name}" is listed more than once.',
  'trait_editor.error.tuple_field_enum': 'List at least one choice for the "{name}" field.',
  'trait_editor.error.tuple_field_name': 'Give every field in the record a name.',
  'trait_editor.error.tuple_fields': 'Add at least one field to the record.',
  'trait_editor.kind.file': 'File',
  'trait_editor.kind.game_date': 'In-world date',
  'trait_editor.kind.game_date_range': 'In-world date span',
  'trait_editor.kind.game_datetime': 'In-world date & time',
  'trait_editor.kind.game_datetime_range': 'In-world date & time span',
  'trait_editor.kind.game_time': 'In-world time',
  'trait_editor.kind.image': 'Image',
  'trait_editor.kind.real_date': 'Real-world date',
  'trait_editor.kind.real_datetime': 'Real-world date & time',
  'trait_editor.kind.real_time': 'Real-world time',
  'trait_editor.kind.tuple': 'Structured record',
  'trait_editor.tuple_field_add': 'Add field to record',
  'trait_editor.tuple_field_name_label': 'Field name',
  'trait_editor.tuple_field_name_placeholder': 'e.g. role, holder, since',
  'trait_editor.tuple_field_optional': 'Optional',
  'trait_editor.tuple_field_remove': 'Remove field',
  'units_editor.add_dimension': 'Add dimension',
  'units_editor.add_unit': 'Add unit',
  'units_editor.close': 'Close',
  'units_editor.description': 'Define measurement dimensions and units for this campaign. Each unit converts to its dimension’s canonical base value, so values stored in different units stay comparable and lossless.',
  'units_editor.dimension_choose': 'Choose a dimension…',
  'units_editor.dimension_label': 'Dimension',
  'units_editor.dimension_name_label': 'Dimension name',
  'units_editor.dimension_name_placeholder': 'e.g. Length',
  'units_editor.dimension_section': 'Dimensions',
  'units_editor.error.dimension_name': 'Enter a dimension name.',
  'units_editor.error.unit_dimension': 'Choose the dimension this unit belongs to.',
  'units_editor.error.unit_name': 'Enter a unit name.',
  'units_editor.error.unit_symbol': 'Enter a unit symbol.',
  'units_editor.error.unit_to_base': 'Enter a valid conversion factor (a decimal like 1000, or a fraction like 5/9).',
  'units_editor.existing_dimensions': 'Defined dimensions',
  'units_editor.existing_units': 'Defined units',
  'units_editor.heading': 'Campaign units',
  'units_editor.is_base_label': 'This is the dimension’s base unit',
  'units_editor.offset_label': 'Offset (optional)',
  'units_editor.offset_placeholder': 'e.g. 0, or -160/9 for °F',
  'units_editor.retire_dimension': 'Retire dimension',
  'units_editor.retire_unit': 'Retire unit',
  'units_editor.symbol_label': 'Symbol',
  'units_editor.symbol_placeholder': 'e.g. m',
  'units_editor.to_base_label': 'Conversion to base',
  'units_editor.to_base_placeholder': 'e.g. 1000, or 5/9',
  'units_editor.unit_name_label': 'Unit name',
  'units_editor.unit_name_placeholder': 'e.g. Metre',
  'units_editor.unit_section': 'Units',
  // Per-trait labels + one-line descriptions. Sourced from the official-core
  // pack's trait declarations (`packages/shared/src/packs/official-core.json`).
  // The compiler builds the picker from the same trait set, so a new
  // trait shipped in that pack lands without a code change here —
  // missing keys fall back to a humanised id (see traitLabel /
  // traitDescription in `entities/trait-attach-view.ts`).
  'trait.inhabited.label': 'Inhabited',
  'trait.inhabited.description': 'A populated place with population and a form of government.',
  'trait.campaign.label': 'Campaign',
  'trait.campaign.description': 'Marks a Lore as the operational shell for a campaign — carries the canonical calendar, accent, voice, and settings.',
  'trait.governed.label': 'Governed',
  'trait.governed.description': 'A locale or faction with a ruler, capital, and codified laws.',
  'trait.embodied.label': 'Embodied',
  'trait.embodied.description': 'Has a physical form — species, gender, appearance, portrait.',
  'trait.mortal.label': 'Mortal',
  'trait.mortal.description': 'Subject to birth and death — carries born_in, died_in, cause_of_death.',
  'trait.notable.label': 'Notable',
  'trait.notable.description': 'Recognised for something specific — claim to fame, reputation, known_for.',
  'trait.magical.label': 'Magical',
  'trait.magical.description': 'Carries magical properties, a school, and what it is bound to.',
  'trait.scheduled.label': 'Scheduled',
  'trait.scheduled.description': 'An event that occurred at a specific time and place with participants.',
  'trait.quest.label': 'Quest',
  'trait.quest.description': 'A goal-bearing event with an objective, reward, status, and giver.',
  'trait.session.label': 'Session',
  'trait.session.description': 'A play session with a real-world span, a summary, and a session number.',
  'trait.traveler.label': 'Traveler',
  'trait.traveler.description': 'A character who has visited places — tracks the visited graph.',

  // #85 — /ui-gallery component explorer (living design-system docs).
  // Section + state labels flow through t() like every other UI string;
  // the component names themselves stay literal mono tokens in the view.
  'ui_gallery.title': 'Component gallery',
  'ui_gallery.intro':
    'Living reference for the @dl/ui design-system primitives, rendered on the client’s own runtime. Toggle the theme to preview light and dark.',
  'ui_gallery.atoms_heading': 'Atoms',
  'ui_gallery.molecules_heading': 'Molecules',
  'ui_gallery.editing_heading': 'Editing surfaces',
  'ui_gallery.state.edit': 'Edit mode',
  // Editing-surfaces section.
  'ui_gallery.fact_field.desc':
    'The controls the fact-row editor mounts per value kind (the live host-wired row lives in the app).',
  'ui_gallery.fact_field.text': 'Text',
  'ui_gallery.fact_field.number': 'Number',
  'ui_gallery.fact_field.rich_text': 'Long text (rendered)',
  'ui_gallery.fact_field.enum': 'Choice list',
  'ui_gallery.fact_field.ref': 'Link to entity',
  'ui_gallery.fact_field.boolean': 'Yes / No',
  'ui_gallery.fact_field.date': 'In-world date',
  'ui_gallery.fact_field.tuple': 'Structured record',
  'ui_gallery.fact_field.tuple_note_sample': 'Appointed at the Founding',
  'ui_gallery.trait_field.desc':
    'The per-predicate authoring controls from the trait editor: name, value kind, cardinality, and choices.',
  'ui_gallery.trait_field.name': 'Field name',
  'ui_gallery.trait_field.kind': 'Value kind',
  'ui_gallery.trait_field.cardinality': 'Cardinality',
  'ui_gallery.trait_field.single': 'Single value',
  'ui_gallery.trait_field.many': 'Many values',
  'ui_gallery.trait_field.choices': 'Choices (enum)',
  'ui_gallery.validity_write.desc':
    'In-world validity range in write mode — from/to bounds, open-ended toggles, and the precision selector.',
  // Live trait-editor specimen (gallery-local build preview + sub-field labels).
  'ui_gallery.trait_build.label': 'Build preview:',
  'ui_gallery.trait_build.ok': 'Would emit {n, plural, one {# predicate} other {# predicates}} + 1 trait definition.',
  'ui_gallery.trait_build.err_trait': 'Name the trait.',
  'ui_gallery.trait_build.err_fields': 'Add at least one field.',
  'ui_gallery.trait_build.err_name': 'Every field needs a name.',
  'ui_gallery.trait_build.err_enum': 'List at least one choice for the enum field.',
  'ui_gallery.trait_build.err_tuple': 'Add at least one field to the record.',
  'ui_gallery.trait_build.err_tuple_name': 'Give every record sub-field a name.',
  'ui_gallery.trait_build.sub_name': 'Sub-field name',
  'ui_gallery.trait_build.optional': 'optional',
  'ui_gallery.trait_build.remove': 'Remove',
  'ui_gallery.trait_build.remove_field': 'Remove field',
  'ui_gallery.trait_build.remove_sub': 'Remove sub-field',
  'ui_gallery.state.default': 'Default',
  'ui_gallery.state.hover': 'Hover',
  'ui_gallery.state.active': 'Active',
  'ui_gallery.state.disabled': 'Disabled',
  'ui_gallery.state.empty': 'Empty',
  'ui_gallery.state.read': 'Read mode',
  'ui_gallery.entity_icon.desc': 'Per-entity-root Lucide marks, painted in currentColor.',
  'ui_gallery.status_pill.desc': 'Fact-status chips across the canon spectrum.',
  'ui_gallery.reveal_chip.desc': 'Per-fact visibility states, glyph-only and labelled.',
  'ui_gallery.provenance_badge.desc': 'Who authored a fact — GM, AI, player, or system.',
  'ui_gallery.card.desc': 'Surface panel variants and padding scale.',
  'ui_gallery.form_field.desc': 'Label, control, help and error chrome.',
  'ui_gallery.select_input.desc': 'Native select with token-correct sizing.',
  'ui_gallery.color_picker.desc':
    'Curated swatch row over a native picker + hex field; the active swatch and hex string are non-color cues.',
  'ui_gallery.color_picker.specimen.selected': 'Swatch selected',
  'ui_gallery.tabs.desc': 'Keyboard-conformant tablist with optional counts.',
  'ui_gallery.collapse.desc': 'Native disclosure with a rotating caret.',
  'ui_gallery.theme_toggle.desc': 'Tri-state light / dark / system cycle button.',
  'ui_gallery.entity_card.desc': 'The reusable entity row — icon tile, name, meta, trailing slot.',
  'ui_gallery.validity_editor.desc':
    'In-world validity range — editable bounds with the folded-in date precision picker.',
  'ui_gallery.asset_field.desc': 'Image asset slot, shown in its empty state.',
  'ui_gallery.calendar_date_picker.desc':
    'A date picker that renders ANY world calendar from a real CalendarDefinition — week or weekless, intercalary festival days, leap days, forward and backward (BCE-style) eras, deep-history negative years, and optional sub-day periods. Interactive: step months, open the month/era controls, pick a day.',
  'ui_gallery.cdp.gregorian': 'Gregorian · 7-day week · BCE/CE eras',
  'ui_gallery.date_field.desc':
    'The compact field shell around the calendar grid: a trigger showing the formatted date that opens the picker in an anchored popover. This is the control wired into the top-bar cursors, game-date facts, and validity bounds. Click to open; pick a day to commit and close.',
  'ui_gallery.date_field.interactive': 'Interactive · trigger + popover grid',
  'ui_gallery.calendar_editor.desc':
    'Authors a fictional-calendar CalendarDefinition with a LIVE preview embedding the calendarDatePicker. The list-first manager (Edit / Fork / Inspect, single primary) is the entry point; the section editor exposes months, leap rule, eras, weekday cycle, sub-day periods, and the anchor. Editing a month length re-renders the embedded preview instantly.',
  'ui_gallery.calendar_editor.manager': 'List-first manager',
  'ui_gallery.calendar_editor.empty': 'Manager · empty state',
  'ui_gallery.calendar_editor.editor': 'Section editor · live preview',
  // Demo calendar field VALUES (user data in the real app — sample content
  // here, routed through t() so the gallery source stays English-sweep clean).
  'ui_gallery.calendar_editor.sample.reaches_name': 'Calendar of the Reaches',
  'ui_gallery.calendar_editor.sample.reaches_summary': '12 months · 7-day week · gregorian leap',
  'ui_gallery.calendar_editor.sample.gregorian_summary': 'Earth-standard · 12×{28–31} · 7-day week',
  'ui_gallery.calendar_editor.sample.harptos_summary': 'Forgotten Realms · 12×30 + 5 festivals',

  // calendarEditor chrome — the atom ships English defaults in @dl/ui (it
  // can't import this catalog without a cycle / tripping the English sweep);
  // the gallery threads these localized copies through the atom's `messages`.
  'calendar_editor.list.eyebrow': 'Campaign · Embers of the Reach',
  'calendar_editor.list.title': 'Calendars',
  'calendar_editor.browse_packs': 'Browse packs',
  'calendar_editor.new_calendar': 'New calendar',
  'calendar_editor.edit': 'Edit',
  'calendar_editor.fork': 'Fork',
  'calendar_editor.inspect': 'Inspect',
  'calendar_editor.primary': 'Primary',
  'calendar_editor.bundled': 'Bundled',
  'calendar_editor.plugin_preview': 'Plugin · preview',
  'calendar_editor.single_primary.title':
    "One calendar is the campaign's primary time axis",
  'calendar_editor.single_primary.body':
    'All in-world dates resolve against the primary. Others stay available for conversion but never drive the session cursor.',
  'calendar_editor.empty.title': 'No calendars yet',
  'calendar_editor.empty.body':
    'Most worlds start from a familiar shape and just rename it. Pick a starting point — you can rename months, eras, and the year without touching the structure.',
  'calendar_editor.back_to_simple': 'Back to simple',
  'calendar_editor.advanced': 'Advanced / Custom calendar…',
  'calendar_editor.cancel': 'Cancel',
  'calendar_editor.save': 'Save calendar',
  'calendar_editor.done': 'Done',
  'calendar_editor.identity': 'Identity',
  'calendar_editor.calendar_name': 'Calendar name',
  'calendar_editor.months': 'Months',
  'calendar_editor.months_hint':
    'Ordered list. Edit a length inline, or mark a row intercalary — a festival day outside the month sequence.',
  'calendar_editor.rename_only': 'rename only',
  'calendar_editor.add_month': 'Add month',
  'calendar_editor.mark_intercalary': 'Mark intercalary',
  'calendar_editor.remove': 'Remove',
  'calendar_editor.festival': 'Festival',
  'calendar_editor.leap_rule': 'Leap rule',
  'calendar_editor.leap_none': 'None',
  'calendar_editor.leap_every_n': 'Every N',
  'calendar_editor.leap_julian': 'Julian',
  'calendar_editor.leap_gregorian': 'Gregorian',
  'calendar_editor.leap_every': 'Every',
  'calendar_editor.leap_phase': 'Phase',
  'calendar_editor.leap_hint':
    'The leap day lands on the month flagged with a leap length. Every N reveals an interval and a phase.',
  'calendar_editor.eras': 'Eras',
  'calendar_editor.eras_hint':
    'Ordered, each mapping a canonical start day to a display year. Backward eras count down toward day 0.',
  'calendar_editor.era_name': 'Era name',
  'calendar_editor.abbreviation': 'Abbreviation',
  'calendar_editor.start_day': 'Start day',
  'calendar_editor.start_day_help': 'canonical day this era begins',
  'calendar_editor.year_one': 'Year one',
  'calendar_editor.year_one_help': "first year's display number",
  'calendar_editor.direction': 'Direction',
  'calendar_editor.forward': 'Forward',
  'calendar_editor.backward': 'Backward',
  'calendar_editor.add_era': 'Add era',
  'calendar_editor.weekday_cycle': 'Weekday cycle',
  'calendar_editor.weekday_hint':
    'Ordered labels + a phase picking which weekday lands on day 0. Omit for no-week calendars.',
  'calendar_editor.phase': 'Phase',
  'calendar_editor.phase_help': 'weekday index sitting at canonical day 0',
  'calendar_editor.add_weekday': 'Add weekday',
  'calendar_editor.sub_day': 'Sub-day periods',
  'calendar_editor.sub_day_hint':
    'Named intra-day spans — watches, bells, hours. Used by the session cursor.',
  'calendar_editor.add_period': 'Add period',
  'calendar_editor.period': 'Period name',
  'calendar_editor.anchor': 'Anchor',
  'calendar_editor.anchor_hint':
    'Which canonical day the origin sits on, optionally named as an in-world event. This is what day 0 means in the fiction.',
  'calendar_editor.anchor_day': 'Canonical day',
  'calendar_editor.anchor_event': 'Named event',
  'calendar_editor.live_preview': 'Live preview',
  'calendar_editor.updates_as_you_edit': 'updates as you edit',
  'calendar_editor.reads_to_players': 'Reads to players as',
  'calendar_editor.optional': 'optional',
  'calendar_editor.required': 'required',
  'calendar_editor.length_locked': 'length locked',
  'calendar_editor.days': 'days',
  'calendar_editor.errors_block': 'Fix errors to save · warnings won’t block',
  'calendar_editor.no_errors': 'Canonical-day bijection valid · 0 errors',
  // validation issue messages (resolved via the atom's resolveIssue → t()).
  'calendar_editor.error.name_required': 'A calendar name is required.',
  'calendar_editor.error.month_length': '{name} must be at least 1 day long.',
  'calendar_editor.error.month_leap_length': '{name}: leap length must be a positive number.',
  'calendar_editor.error.era_year_one': '{name}: year one must be a whole number.',
  'calendar_editor.error.era_start_day': '{name}: start day must be a whole number.',
  'calendar_editor.error.era_order': '{name} begins on or before the previous era. Order eras by start day.',
  'calendar_editor.error.anchor_day': 'The anchor day must be a whole number.',
  'calendar_editor.error.anchor_unresolvable': "The anchor doesn't resolve — check the eras and months.",
  'calendar_editor.error.schema': 'The calendar definition is structurally invalid.',
  'calendar_editor.warn.anchor_unnamed':
    'The origin (day 0) has no in-world event. Players will see “day 0” instead of a named founding.',

  'ui_gallery.cdp.harptos': 'Harptos · tenday · intercalary festivals',
  'ui_gallery.cdp.fuzzy': 'Fuzzy precision · folded-in Day/Month/Season/Year toggle',
  'ui_gallery.cdp.harptos_time': 'Harptos · with sub-day watches',
  'ui_gallery.cdp.tidecount': 'The Tidecount · no week → day list',
  'ui_gallery.cdp.deep_history': 'Backward era · deep-history negative year',
  'ui_gallery.sample.location_name': 'Neverwinter',
  'ui_gallery.sample.location_meta': 'Location · city · capital',
  'ui_gallery.sample.character_name': 'Drizzt Do’Urden',
  'ui_gallery.sample.character_meta': 'Character · drow ranger',
  'ui_gallery.sample.field_label': 'Display name',
  'ui_gallery.sample.field_help': 'How this entity is shown across the app.',
  'ui_gallery.sample.field_error': 'A name is required.',
  'ui_gallery.sample.select_label': 'Status',
  'ui_gallery.sample.tabs_label': 'Sample entity tabs',
  // Reveal-state labels for the gallery specimens. The reveal chip needs
  // an accessible label per state; the app's `reveal.chip.*` keys only
  // cover hidden/partial, so the gallery names all four states here.
  'ui_gallery.reveal.public': 'Public — visible to everyone',
  'ui_gallery.reveal.partial': 'Partial — revealed to specific Viewers',
  'ui_gallery.reveal.hidden': 'Hidden — GM only',
  'ui_gallery.reveal.redacted': 'Redacted',
  'ui_gallery.reveal.labelled': 'Public (labelled)',

  // §8 atoms/molecules — Phase A. These primitives carry a deliberately
  // minimal baseline and are flagged "unreviewed" until a design pass.
  'ui_gallery.unreviewed': 'UNREVIEWED',
  'ui_gallery.unreviewed.note':
    'Pending design review — the baseline below is functional and accessible but not the final design.',
  'ui_gallery.button.desc': 'Action control — primary, secondary, ghost, danger, icon.',
  'ui_gallery.text_input.desc': 'Single-line and multi-line text controls, with an invalid state.',
  'ui_gallery.badge.desc': 'Generic count / label / type chip across presentational tones.',
  'ui_gallery.empty_state.desc': 'One canonical empty placeholder — icon, message, optional action.',
  'ui_gallery.page_header.desc': 'Page title with actions, and a level-aware section heading.',
  'ui_gallery.state.primary': 'Primary',
  'ui_gallery.state.secondary': 'Secondary',
  'ui_gallery.state.ghost': 'Ghost',
  'ui_gallery.state.danger': 'Danger',
  'ui_gallery.state.icon': 'Icon',
  'ui_gallery.state.single_line': 'Single line',
  'ui_gallery.state.invalid': 'Invalid',
  'ui_gallery.state.multiline': 'Multi-line',
  'ui_gallery.tone.neutral': 'Neutral',
  'ui_gallery.tone.accent': 'Accent',
  'ui_gallery.tone.info': 'Info',
  'ui_gallery.tone.warn': 'Warn',
  'ui_gallery.tone.success': 'Success',
  'ui_gallery.sample.button_label': 'Save changes',
  'ui_gallery.sample.input_placeholder': 'Type a name…',
  'ui_gallery.sample.badge_count': '12',
  'ui_gallery.sample.badge_label': 'homebrew',
  'ui_gallery.sample.empty_title': 'No entities yet',
  'ui_gallery.sample.empty_message': 'Create your first entity, or let the LLM draft one for you.',
  'ui_gallery.sample.empty_action': 'New entity',
  'ui_gallery.sample.page_title': 'Locations',
  'ui_gallery.sample.page_intro': 'Every place your party has touched, real or rumored.',
  'ui_gallery.sample.section_title': 'Recent reveals',
  'ui_gallery.sample.page_action': 'New location',
  'ui_gallery.state.bare': 'Bare (inline)',
  'ui_gallery.state.overline': 'Overline',
  'ui_gallery.sample.overline_title': 'In this campaign',

  // #257 — Dice roller (07 §13.9.1, session HUD). Entirely ephemeral.
  'dice.heading': 'Dice roller',
  'dice.input.label': 'Dice notation',
  'dice.input.placeholder': 'e.g. 2d6+3, 1d20, 4d6kh3',
  'dice.roll': 'Roll',
  'dice.clear': 'Clear history',
  'dice.error.unknown': 'Could not roll dice — check the notation.',
  'dice.history.label': 'Recent rolls',
  'dice.history.empty': 'No rolls yet.',
  'dice.history.row_label': '{notation}: {breakdown}',
  // #257 — Initiative tracker (07 §13.9.1, session HUD).
  'initiative.heading': 'Initiative',
  'initiative.empty': 'No participants yet.',
  'initiative.add': 'Add participant',
  'initiative.add_form.name_label': 'Name',
  'initiative.add_form.name_placeholder': 'Aria, Goblin #1, …',
  'initiative.add_form.initiative_label': 'Initiative',
  'initiative.add_form.hp_label': 'Max HP (optional)',
  'initiative.add_form.submit': 'Add',
  'initiative.add_form.cancel': 'Cancel',
  'initiative.round': 'Round {n}',
  'initiative.advance': 'Next turn',
  'initiative.reset': 'Reset tracker',
  'initiative.remove': 'Remove',
  'initiative.remove_label': 'Remove {name} from initiative',
  'initiative.entry.label': '{name} (initiative {initiative})',
  'initiative.active_label': 'Active turn: {name}',
  'initiative.hp': 'HP: {current}',
  'initiative.hp_of': 'HP: {current}/{max}',
  'initiative.hp_damage': 'Apply damage (enter a positive number)',
  'initiative.hp_heal': 'Apply healing (enter a positive number)',
  'initiative.damage': '−',
  'initiative.heal': '+',
  'initiative.conditions.label': 'Conditions',
  'initiative.conditions.prone': 'Prone',
  'initiative.conditions.concentration': 'Concentration',
  'initiative.conditions.bleeding': 'Bleeding',
  // #259 — Post-combat log (07 §13.9.2).
  'combat_log.end_encounter': 'End encounter',
  'combat_log.end_encounter_menu_label': 'End encounter options',
  'combat_log.quick_log': 'Quick log',
  'combat_log.open_full_form': 'Full log form',
  'combat_log.cancel': 'Cancel',
  'combat_log.full_form_title': 'Log encounter',
  'combat_log.participants_section': 'Participants',
  'combat_log.fate_for': 'Fate for {name}',
  'combat_log.fate.survived': 'Survived',
  'combat_log.fate.killed': 'Killed',
  'combat_log.fate.fled': 'Fled',
  'combat_log.location_label': 'Location',
  'combat_log.location_placeholder': 'Entity id or leave blank',
  'combat_log.loot_label': 'Loot notes',
  'combat_log.loot_placeholder': 'Describe loot found (will become draft facts)',
  'combat_log.world_state_label': 'World-state notes',
  'combat_log.world_state_placeholder': 'Describe outcomes, consequences (will become draft facts)',
  'combat_log.submit': 'Log encounter',
  // Auditor advisory.
  'auditor.combat.quick_log_unreviewed':
    'Encounter log bundle {bundle_id} has been open for over 30 days — review or dismiss it.',
  // #260 — Narrative combat panel (07 §13.9.5). Off by default; shown when
  // `config.narrative_combat_panel_enabled` is true and initiative is active.
  'narrative_panel.heading': 'Narrative combat panel',
  // Affordance 1 — Reaction roll.
  'narrative_panel.reaction_roll': 'Reaction roll',
  'narrative_panel.reaction_roll.label': 'Roll 2d6 reaction for {name}',
  'narrative_panel.reaction_roll.disposition': '{name} reaction {total} — {disposition}',
  // Affordance 2 — Damage assignment.
  'narrative_panel.damage.label': 'Assign damage to {name}',
  'narrative_panel.damage.input_label': 'Damage amount',
  'narrative_panel.damage.input_placeholder': '0',
  'narrative_panel.damage.apply': 'Apply damage',
  'narrative_panel.damage.draft_label': 'Draft damage narration for {name}',
  // Affordance 3 — Apply condition.
  'narrative_panel.condition.label': 'Apply/remove condition on {name}',
  'narrative_panel.condition.custom_placeholder': 'Custom condition…',
  'narrative_panel.condition.add_custom': 'Add custom condition',
  'narrative_panel.condition.prone': 'Prone',
  'narrative_panel.condition.grappled': 'Grappled',
  'narrative_panel.condition.frightened': 'Frightened',
  'narrative_panel.condition.poisoned': 'Poisoned',
  'narrative_panel.condition.bleeding': 'Bleeding',
  'narrative_panel.condition.staggered': 'Staggered',
  // Affordance 4 — NPC turn quick-pick.
  'narrative_panel.npc_action.label': 'NPC turn action for {name}',
  'narrative_panel.npc_action.attack': 'Attack',
  'narrative_panel.npc_action.move': 'Move',
  'narrative_panel.npc_action.use_ability': 'Use ability',
  'narrative_panel.npc_action.flee': 'Flee',
  'narrative_panel.npc_action.yield': 'Yield',
  // Affordance 5 — Save/skill check.
  'narrative_panel.save.label': 'Roll save/check for {name}',
  'narrative_panel.save.input_label': 'Dice notation',
  'narrative_panel.save.input_placeholder': '1d20',
  'narrative_panel.save.roll': 'Roll',
  'narrative_panel.save.cancel': 'Cancel',
  // Affordance 6 — End-turn-with-effect.
  'narrative_panel.effect.label': 'Tag end-turn effect for {name}',
  'narrative_panel.effect.input_label': 'Effect',
  'narrative_panel.effect.input_placeholder': 'moved, attacked, used spell slot…',
  'narrative_panel.effect.submit': 'Tag effect',
  'narrative_panel.effect.cancel': 'Cancel',
  // Draft narration.
  'narrative_panel.draft_narration': 'Draft narration',
  'narrative_panel.draft_narration.label': 'Submit as draft narration fact',
  'narrative_panel.draft_narration.sent': 'Draft sent to inbox',
  // Turn log.
  'narrative_panel.turn_log.heading': 'Turn log',
  'narrative_panel.turn_log.empty': 'No actions recorded this encounter.',
  'narrative_panel.turn_log.round_label': 'Round {n}',
  'narrative_panel.turn_log.entry_label': '{kind}: {description}',
  // Campaign roster + invite panel (#263).
  'settings.roster.heading': 'Members & Invites',
  'settings.roster.help': 'Manage who has access to this campaign.',
  'settings.roster.member_email': 'Email',
  'settings.roster.member_role': 'Role',
  'settings.roster.role.owner': 'Owner',
  'settings.roster.role.gm': 'GM',
  'settings.roster.role.player': 'Player',
  'settings.roster.remove': 'Remove',
  'settings.roster.empty': 'No additional members yet.',
  'settings.roster.invite_email.heading': 'Invite by email',
  'settings.roster.invite_email.label': 'Email address',
  'settings.roster.invite_email.placeholder': 'player@example.com',
  'settings.roster.invite_email.role_label': 'Role',
  'settings.roster.invite_email.send': 'Send invite',
  'settings.roster.invite_email.sending': 'Sending…',
  'settings.roster.invite_link.heading': 'Shareable link',
  'settings.roster.invite_link.create': 'Create player link',
  'settings.roster.invite_link.create_gm': 'Create GM link',
  'settings.roster.invite_link.close': 'Close link',
  'settings.roster.invite_link.copy': 'Copy link',
  'settings.roster.invite_link.copied': 'Copied!',
  'settings.roster.invite_link.uses': '{count, plural, one {# use} other {# uses}}',
  'settings.roster.pending_invites.heading': 'Pending invites',
  'settings.roster.pending_invites.revoke': 'Revoke',
  'settings.roster.pending_invites.empty': 'No pending invites.',
  'settings.roster.active_links.heading': 'Active links',
  'settings.roster.active_links.empty': 'No active links.',

  // ── #266 — flat co-author surfacing ──────────────────────────────────────
  // Flat Campaign home (07 §2.2 / workspace-home-flat.html). No hierarchical
  // framing: members are co-authors, not GM/Players.
  'flat_home.heading': 'Recent additions to the world',
  'flat_home.subheading':
    "Every member's writes go to canon directly. Below: the latest facts added, by author.",
  'flat_home.empty': 'No facts yet. Start writing to see contributions here.',
  'flat_home.syncing': 'Syncing…',
  'flat_home.members_heading': 'Co-authors',
  'flat_home.members_subheading': 'No roles, no hierarchy. Counts shown for context, not authority.',
  'flat_home.members_empty': 'No member data yet.',
  'flat_home.member_facts': '{count, plural, one {# fact} other {# facts}}',
  // Co-authorship provenance chip shown alongside each fact row.
  'flat_home.authored_by': '{author}',
  // Mode notice strip (mirrors workspace-home-flat.html banner).
  'flat_home.mode_notice':
    "Flat mode · no hierarchy. Every member's writes auto-ratify to canon. The LLM still emits drafts — the \"LLM never writes canon\" invariant holds.",
  'flat_home.mode_notice_label': 'Flat facilitation mode',

  // Draft inbox in flat mode: LLM drafts route to "next-session prep"
  // rather than a "GM inbox" (07 §10 note; workspace-home-flat.html rail).
  'inbox.flat.open': 'Session prep',
  'inbox.flat.title': 'Session prep',
  'inbox.flat.onboarding':
    'AI-generated suggestions appear here for group review before your next session. Accept or discard — the group is always the source of truth.',
  'inbox.flat.empty': 'No suggestions waiting.',

  // #264 — per-member Character assignment display in the roster
  'settings.roster.characters.label': 'Characters',
  'settings.roster.characters.none': 'No characters assigned',
};

const catalogs: Record<string, Record<string, string>> = { en: EN };

let active = 'en';

/**
 * Compiled-formatter cache. Key is `${locale}\x00${key}`.
 *
 * `null` means "parsed once and known to be plain — fast-path the
 * template substitution and skip ICU entirely". This avoids the
 * IntlMessageFormat instantiation cost for the ~70% of catalog keys
 * that don't carry plural / select syntax.
 */
const formatterCache = new Map<string, IntlMessageFormat | null>();

function isPlainTemplate(template: string): boolean {
  // A plain template has no ICU brace contents (no comma inside any
  // `{...}` block). Pure `{name}` substitutions parse as ICU but we can
  // short-circuit them faster.
  let depth = 0;
  for (const ch of template) {
    if (ch === '{') depth++;
    else if (ch === '}') depth--;
    else if (depth > 0 && ch === ',') return false;
  }
  return true;
}

function substitutePlain(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const v = vars[name];
    return v === undefined ? match : String(v);
  });
}

const MF_OPTIONS: MFOptions = {
  // We don't author HTML tags in catalog strings (XSS-prone in a render
  // loop that hands strings to textContent). Disable tag parsing.
  ignoreTag: true,
};

function compile(locale: string, key: string, template: string): IntlMessageFormat | null {
  const cacheKey = `${locale}\x00${key}`;
  if (formatterCache.has(cacheKey)) return formatterCache.get(cacheKey) ?? null;
  if (isPlainTemplate(template)) {
    formatterCache.set(cacheKey, null);
    return null;
  }
  const mf = new IntlMessageFormat(template, locale, undefined, MF_OPTIONS);
  formatterCache.set(cacheKey, mf);
  return mf;
}

export function setLocale(locale: string): void {
  if (!catalogs[locale]) throw new Error(`unknown locale: ${locale}`);
  active = locale;
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const catalog = catalogs[active] ?? EN;
  const hasKey = Object.prototype.hasOwnProperty.call(catalog, key);
  if (!hasKey) return key;
  const template = catalog[key] ?? key;
  if (!vars && isPlainTemplate(template)) return template;
  const mf = compile(active, key, template);
  if (mf === null) {
    return vars ? substitutePlain(template, vars) : template;
  }
  const formatted = mf.format(vars ?? {});
  // IntlMessageFormat returns string | string[]; for tag-free templates
  // (we pass `ignoreTag: true`) it's always a string, but the typed
  // surface is the union — coerce defensively.
  return Array.isArray(formatted) ? formatted.join('') : String(formatted);
}

/** Inject or override a catalog at runtime (test fixtures + Phase 2 locales). */
export function registerCatalog(locale: string, catalog: Record<string, string>): void {
  catalogs[locale] = { ...(catalogs[locale] ?? {}), ...catalog };
  // Bust any compiled formatters for this locale.
  for (const cacheKey of Array.from(formatterCache.keys())) {
    if (cacheKey.startsWith(`${locale}\x00`)) formatterCache.delete(cacheKey);
  }
}

/** For tests / future locale tooling — list the keys an active catalog declares. */
export function catalogKeys(locale: string = active): string[] {
  return Object.keys(catalogs[locale] ?? EN);
}

/** For tests — peek at the active locale (mainly for assertions). */
export function activeLocale(): string {
  return active;
}
